import { CommonModule } from '@angular/common';
import { isSearchCompleted } from '../utils/graph-search-completion.util';
import { linkFlowArrow, LinkFlowState } from '../utils/graph-link-flow.util';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import * as L from 'leaflet';
import {
  EntityType,
  NodeActionItem,
  NodeTag,
  PaletteAction,
  SearchableValueRow,
  SearchValueOption,
  TreeNode,
  ViewerGraphData,
  ViewerNode,
  ViewerEdge,
  ViewMode,
} from '../models/graph-viewer.models';
import { GraphAnnotationsService, TAG_META } from '../services/graph-annotations.service';
import { LinkAnalysisExportService } from '../../../../services/link-analysis/link-analysis-export.service';
import { ThemeService } from '../../../../services/theme/theme.service';
import { expandGraphWithSourceTypeHubs, isSourceTypeHubNode, resolveNodeSourceType } from '../../../../services/link-analysis/link-analysis-source-hub.util';
import {
  createGraphPlatformIconDataUrl,
  resolvePlatformIconVisual,
} from '../../../../services/link-analysis/platform-icon.util';
import { getSimpleIconBrandColor } from '../../../../services/link-analysis/simple-icons-brand.util';
import { collectDossierImageSources } from '../../../../services/link-analysis/link-analysis-pdf-export.util';
import {
  buildAdjacency,
  buildHierarchy,
  detectCorrelations,
  deriveMeta,
  entitiesToCSV,
  ENTITY_COLORS,
  ENTITY_ICONS,
  ENTITY_LABELS,
  exportToJSON,
  getBrand,
  getDegreeMap,
  collectTreeSubtreeIds,
  getHierarchyEdges,
  getHierarchySelectionFocusIds,
  getHopDistances,
  getTimelineDates,
  LAYER_ORDER,
  ensureNamesAndPhotosCollectionHubs,
  mapPayloadToViewerGraph,
  pruneTreeForVisibleNodes,
  relationshipsToCSV,
  resolveDefaultRootId,
} from '../utils/graph-viewer-data.util';
import { computeSearchTrunkLayout, graphEndpointId, groupSearchTrunks, searchFanGeometry, searchGroupDragMembers, SearchTrunkGroup } from '../utils/graph-search-trunks.util';
import { foldCollectedPhotoNodes } from '../utils/graph-collected-photos.util';
import {
  GRAPH_EDGE_ACTIVE,
  GRAPH_EDGE_COLOR_DARK,
  GRAPH_EDGE_COLOR_LIGHT,
  getGraphCategoryLabel,
  getGraphIconColor,
  getGraphPointerBounds,
  insetLine,
  paintArrowHead,
  paintBookmarkRibbon,
  paintBrandGlyph,
  paintGraphNodeLabels,
  paintGraphTypeIcon,
  paintPlatformBrandIcon,
  paintSelectionHalo,
  paintSquarePhoto,
} from '../utils/graph-maltego-paint.util';
import {
  buildDetailFieldSearchActions,
  buildNodeDetailPanel,
  collectNodeDetailImages,
  buildOsintStyleCard,
  IDENTITY_HUB_ACCENTS,
  isIdentityHubNode,
  isIdentityHubChildNode,
  NodeDetailField,
  NodeDetailPanel,
  NodeDetailSection,
  NodeDetailStat,
  isPhotoHubCard,
  OsintStyleCard,
  OsintStyleRelatedItem,
  resolveDetailFieldSearchValue,
  resolveIdentityHubType,
  uniqueOsintPhotoTiles,
  osintImageDedupeKey,
} from '../utils/node-detail-panel.util';
import { collectGraphMapMarkers, GraphMapMarker } from '../utils/graph-map-points.util';
import { serviceNodePrimaryValue } from '../utils/graph-service-node.util';
import { shouldDisplayOsintSourceOnGraph } from '../utils/osint-search-parity.util';
import {
  canStartImageSearch as canStartImageSearchUtil,
  isAuthGatedOsintImageUrl,
  navigateToOsintImageSearch,
  resolveImageSearchSrc,
  toAbsoluteOsintImageUrl,
  type ImageSearchCandidate,
} from '../../../../services/osint-search/image-search-from-result.util';
import {
  getAggregateItems,
  getNodeSearchableValues,
  getServerActionsForValueType,
  isAggregateHubNode,
} from '../../../../services/link-analysis/link-analysis-searchable.util';
import {
  DEFAULT_PROFILE_AVATAR_SRC,
  SearchImage,
  SearchResultProcessorService,
} from '../../../../services/search-result-processor/search-result-processor.service';

interface PhotosHubTile {
  image: SearchImage;
  nodeId?: string;
}

@Component({
  selector: 'app-link-analysis-graph-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './graph-dashboard.component.html',
  styleUrls: ['./graph-dashboard.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class LinkAnalysisGraphDashboardComponent
  implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer') mapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('detailsPanel') detailsPanel?: ElementRef<HTMLElement>;
  @ViewChild('sourcePictureLink') sourcePictureLink?: ElementRef<HTMLAnchorElement>;

  @ViewChild('graphContainer')
  set graphContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    const el = ref?.nativeElement;
    if (!el) {
      this.graphContainerEl = undefined;
      return;
    }
    if (el === this.graphContainerEl) return;
    this.graphContainerEl = el;
    this.observeGraphContainer();
    if (!this.graph || !this.isGraphDomMounted()) {
      this.queueGraphSync();
    } else {
      this.resizeGraph();
      this.graph?.refresh?.();
    }
  }

  private graphContainerEl?: HTMLDivElement;
  private graphInitPromise: Promise<void> | null = null;
  private themeSubscription: Subscription | null = null;
  isLightTheme = false;

  @Input() graphPayload: any = null;
  @Input() investigation: any = null;
  @Input() loading = false;
  @Input() loadingMessage = 'Loading graph engine…';
  @Input() backgroundProcessing = false;
  @Input() backgroundProcessingMessage = '';
  @Input() errorMessage = '';
  @Input() title = 'OSINT Link Analysis';
  @Input() subtitle = '';
  @Input() nodeActions: NodeActionItem[] = [];
  @Input() selectedActionKeys: string[] = [];
  @Input() actionRadioGroupName = 'node-action';
  @Input() showSearchPanel = false;
  @Input() searchAvailabilityLabel = '';
  @Input() showSearchValuePicker = false;
  @Input() searchValueOptions: SearchValueOption[] = [];
  @Input() selectedSearchValues: string[] = [];
  @Input() searchableValueRows: SearchableValueRow[] = [];
  @Input() showStarterSearch = false;
  @Input() starterQueryType: 'phone' | 'email' | 'username' = 'phone';
  @Input() starterQueryValue = '';
  @Input() starterActionRunning = false;
  @Input() canRunAi = false;
  @Input() aiRunning = false;
  @Input() showBackButton = true;
  @Input() graphFullscreenOpen = false;
  @Input() showStopAutomation = false;
  @Input() stoppingAutomation = false;

  @Output() back = new EventEmitter<void>();
  @Output() regenerate = new EventEmitter<void>();
  @Output() stopAutomation = new EventEmitter<void>();
  @Output() nodeSelect = new EventEmitter<any>();
  @Output() addNodeRequested = new EventEmitter<void>();
  @Output() runAction = new EventEmitter<NodeActionItem>();
  /** Detail-card search icon → prepare popover (value + actions), do not auto-run. */
  @Output() prepareDetailFieldSearch = new EventEmitter<{
    queryValue: string;
    queryType: NonNullable<NodeDetailField['searchQueryType']>;
    sourceNodeId?: string;
  }>();
  /** Popover closed or node re-activated — clear detail-field force-value. */
  @Output() clearDetailFieldSearch = new EventEmitter<void>();
  /** Aggregate entry click → open owning source + preselect that value (R12). */
  @Output() jumpToSourceValue = new EventEmitter<{
    sourceNodeId: string;
    value: string;
    valueType?: string;
  }>();
  @Output() actionKeySelect = new EventEmitter<{ key: string; selected: boolean }>();
  @Output() runSelectedAction = new EventEmitter<void>();
  @Output() searchValueSelect = new EventEmitter<{ value: string; selected: boolean }>();
  @Output() starterQueryTypeSelect = new EventEmitter<'phone' | 'email' | 'username'>();
  @Output() starterQueryValueChange = new EventEmitter<string>();
  @Output() runStarterSearch = new EventEmitter<void>();
  @Output() runAi = new EventEmitter<void>();
  @Output() galleryUpdated = new EventEmitter<void>();

  readonly layerOrder = LAYER_ORDER;
  readonly entityColors = ENTITY_COLORS;
  readonly entityIcons = ENTITY_ICONS;
  readonly entityLabels = ENTITY_LABELS;
  readonly tagMeta = TAG_META;
  readonly tagKeys: NodeTag[] = ['confirmed', 'review', 'false_positive'];

  graphData: ViewerGraphData = { nodes: [], edges: [] };
  filteredNodes: ViewerNode[] = [];
  filteredLinks: any[] = [];
  /** Node ids that have at least one child in the visible hierarchy tree. */
  treeBranchNodeIds = new Set<string>();
  degreeMap: Record<string, number> = {};
  adjacency = new Map<string, Set<string>>();
  hopDistances: Record<string, number> = {};
  hierarchyEdges: ViewerEdge[] = [];
  private searchTrunkByLink = new Map<any, SearchTrunkGroup<any>>();
  private treeTrunkRailOffsets = new Map<string, number>();
  correlations = detectCorrelations({ nodes: [], edges: [] });
  timelineDates: string[] = [];
  tree: TreeNode | null = null;
  insightsTypeDist: Record<string, number> = {};
  insightsTopNodes: Array<{ node: ViewerNode; degree: number }> = [];
  insightsRiskCounts: Record<string, number> = { high: 0, medium: 0, low: 0 };
  insightsMaxType = 1;
  insightsMaxDegree = 1;

  selectedNode: ViewerNode | null = null;
  hoverNodeId: string | null = null;
  /** Full entity name while hovering (shown in HTML tooltip). */
  hoverTooltipLabel: string | null = null;
  hoverTooltipX = 0;
  hoverTooltipY = 0;
  highlightedNodeId: string | null = null;
  /** Under-node labels appear only at/above this ForceGraph zoom scale. */
  private readonly nodeLabelZoomThreshold = 0.55;
  /** Truncated under-node label length (full name stays on hover). */
  private readonly nodeLabelMaxChars = 22;
  visibleLayers = new Set<EntityType>(LAYER_ORDER);
  isPanelOpen = false;
  isPanelCollapsed = false;
  infoPopoverOpen = false;
  private infoPopoverCloseTimer: ReturnType<typeof setTimeout> | null = null;
  viewMode: ViewMode = 'tree';
  treePanelOpen = false;
  /** Collapsible branch layers in hierarchy view (all selected/expanded by default). */
  branchLayers: Array<{ id: string; label: string; number: number; color: string }> = [];
  expandedBranchIds = new Set<string>();
  branchLayersCollapsed = true;
  entityLayersCollapsed = true;
  insightsOpen = false;
  animateLinks = true;
  private linkFlowSeconds = 0;
  private linkFlowStates = new WeakMap<object, LinkFlowState>();
  isolatedId: string | null = null;
  searchPopover: {
    nodeId: string;
    node: ViewerNode;
    x: number;
    y: number;
    pinned: boolean;
  } | null = null;
  /**
   * When set, the node-search popover is scoped to one detail-card field:
   * only that value + actions for its query type (OSINT / Leak / Face).
   */
  detailFieldSearchFocus: {
    queryType: NonNullable<NodeDetailField['searchQueryType']>;
    queryValue: string;
  } | null = null;
  private searchPopoverDragState: {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
  } | null = null;
  private searchPopoverDragMoveHandler: ((event: PointerEvent) => void) | null = null;
  private searchPopoverDragEndHandler: ((event: PointerEvent) => void) | null = null;
  timelineOpen = false;
  timelineVisibleIds: Set<string> | null = null;
  correlationOpen = false;
  correlationHighlight = new Set<string>();
  rootId = '';
  hopFilter: number | null = null;
  cmdOpen = false;
  mapOpen = false;
  mapLoading = false;
  mapMarkerCount = 0;
  exportOpen = false;
  exportingPdf = false;
  graphEngineError = '';
  searchQuery = '';
  noteDraft = '';
  noteSaved = false;
  detailsPanelWidth = this.getResponsiveDetailsPanelWidth();
  resizingDetailsPanel = false;
  failedNodeImages = new Set<string>();
  failedDetailImages = new Set<string>();
  private readonly failedFaceCardImages = new Set<string>();
  faceCardImageUnavailable = false;
  osintGalleryImages: SearchImage[] = [];
  private osintImagesSub: Subscription | null = null;
  private osintImageUrlSignature = '';
  private platformImageUrls = new Map<string, string[]>();
  private sourcePictureSelections = new Map<string, string>();

  readonly detailsPanelMinWidth = 320;
  private readonly detailsPanelDefaultShare = 0.36;
  private readonly detailsPanelDefaultMaxShare = 0.4;
  private readonly detailsPanelResizeMaxShare = 0.55;

  timelineStepIndex = 0;
  timelinePlaying = false;
  timelineNewAtStep: ViewerNode[] = [];

  cmdQuery = '';
  cmdFocusedIndex = 0;
  cmdItems: Array<{ type: 'action' | 'node'; label: string; hint?: string; run: () => void }> = [];

  private graph?: any;
  private imageCache = new Map<string, HTMLImageElement>();
  private selectedDetailCache?: { node: ViewerNode; graph: ViewerGraphData; panel: NodeDetailPanel; card?: OsintStyleCard };
  private sourcePicturesCache?: { card: OsintStyleCard; gallery: SearchImage[]; failures: number; pictures: SearchImage[] };
  private galleryLookupCache?: { gallery: SearchImage[]; urls: Map<string, SearchImage | undefined> };
  private serviceNodePresentation = new WeakMap<object, { value: string; images: string[] }>();
  private imageRequests = new Map<string, string>();
  private selectionFocusCache?: {
    id: string; mode: string; tree: TreeNode | null; nodes: ViewerNode[];
    adjacency: Map<string, Set<string>>; value: Set<string>;
  };
  private neighborFocusCache?: { id: string; adjacency: Map<string, Set<string>>; value: Set<string> };
  private indexedTree?: TreeNode | null;
  private hierarchyNodeIndex = new Map<string, TreeNode>();
  private imagesReady = 0;
  private map?: L.Map;
  private timelineInterval: ReturnType<typeof setInterval> | null = null;
  private forceApplyTimer: ReturnType<typeof setTimeout> | null = null;
  private graphSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private graphResizeObserver: ResizeObserver | null = null;
  private detailsResizeStartX = 0;
  private detailsResizeStartWidth = 320;
  private layoutRefreshRaf: number | null = null;
  private graphDataSignature = '';
  private initialViewportApplied = false;
  private treeLayoutDirty = true;
  private pendingTreeLayoutAnchor: { id: string; x: number; y: number } | null = null;
  private layoutInvestigationId = '';
  private networkNodePositions = new Map<string, { x: number; y: number }>();
  private treeNodePositions = new Map<string, { x: number; y: number; fx?: number; fy?: number }>();
  /** Compact hierarchy positions before zoom-in spread. */
  private treeCompactPositions = new Map<string, { x: number; y: number }>();
  private treeZoomSpreadApplied = 1;
  /** Drawing geometry only; these handles are never graph nodes or relationships. */
  private junctionPositions = new Map<string, { x: number; y: number }>();
  private searchJunctionNodes = new Map<string, any>();
  /** Zoom above this gets a tiny horizontal spread so siblings are easier to tell apart. */
  private readonly treeZoomGapThreshold = 1.45;
  private branchLayersSignature = '';
  /**
   * Active hierarchy subtree drag: moving a node also translates all descendants
   * so branches can be rearranged as a rigid group.
   */
  private subtreeDragSession: {
    rootId: string;
    memberIds: string[];
    origins: Map<string, { x: number; y: number }>;
    dragStart: { x: number; y: number };
  } | null = null;

  private static readonly COLLAPSIBLE_SOURCE_TYPES = new Set(['osint', 'ai_analysis', 'leak']);
  private static readonly BRANCH_LAYER_COLORS: Record<string, string> = {
    osint: '#14b8a6',
    ai_analysis: '#8b5cf6',
    leak: '#ef5350',
  };

  constructor(
    private toastr: ToastrService,
    private annotations: GraphAnnotationsService,
    private linkAnalysisExportService: LinkAnalysisExportService,
    private themeService: ThemeService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private searchResultProcessor: SearchResultProcessorService,
    private router: Router,
  ) {
    this.isLightTheme = this.themeService.currentTheme.value === 'light-theme';
    this.themeSubscription = this.themeService.currentTheme$.subscribe((theme) => {
      const nextLight = theme === 'light-theme';
      if (this.isLightTheme === nextLight) return;
      this.isLightTheme = nextLight;
      this.applyGraphTheme();
    });
  }

  ngAfterViewInit(): void {
    this.refreshDerivedData();
    this.observeGraphContainer();
    this.detailsPanelWidth = this.getResponsiveDetailsPanelWidth();
    this.queueGraphSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphPayload'] || changes['loading'] || changes['backgroundProcessing']) {
      this.refreshDerivedData();
      this.queueGraphSync();
    }
    if (changes['loading'] && !this.loading && this.graphData.nodes.length) {
      this.queueGraphSync();
    }
    if (changes['graphFullscreenOpen']) {
      this.scheduleLayoutRefresh();
      this.queueGraphSync();
    }
    if (
      changes['nodeActions'] ||
      changes['selectedActionKeys'] ||
      changes['showSearchPanel'] ||
      changes['showSearchValuePicker'] ||
      changes['searchableValueRows'] ||
      changes['selectedSearchValues']
    ) {
      const nodeId = this.searchPopover?.nodeId ?? this.selectedNode?.id;
      if (nodeId) {
        this.refreshSearchPopoverActions(nodeId);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.forceApplyTimer) clearTimeout(this.forceApplyTimer);
    if (this.graphSyncTimer) clearTimeout(this.graphSyncTimer);
    if (this.timelineInterval) clearInterval(this.timelineInterval);
    if (this.layoutRefreshRaf != null) cancelAnimationFrame(this.layoutRefreshRaf);
    this.clearInfoPopoverCloseTimer();
    this.graphResizeObserver?.disconnect();
    this.themeSubscription?.unsubscribe();
    this.osintImagesSub?.unsubscribe();
    this.stopSearchPopoverDrag();
    this.destroyMap();
    this.destroyGraph();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.cmdOpen = !this.cmdOpen;
      if (this.cmdOpen) this.refreshCmdItems();
    }
    if (event.key === 'Escape') {
      this.closeCommandPalette();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncDetailsPanelWidth();
    this.scheduleLayoutRefresh();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMousemove(event: MouseEvent): void {
    if (!this.resizingDetailsPanel) return;
    const delta = this.detailsResizeStartX - event.clientX;
    this.detailsPanelWidth = this.clampDetailsPanelWidth(this.detailsResizeStartWidth + delta);
    this.cdr.detectChanges();
    this.scheduleLayoutRefresh();
  }

  @HostListener('document:mouseup')
  onDocumentMouseup(): void {
    const wasResizing = this.resizingDetailsPanel;
    this.resizingDetailsPanel = false;
    if (wasResizing) {
      this.scheduleLayoutRefresh();
      setTimeout(() => this.refreshGraphAfterPanelChange(), 0);
    }
  }

  get annotationsMap(): Record<string, { tag?: NodeTag; note?: string }> {
    return this.annotations.getAnnotations();
  }

  /** Hover focus when nothing is selected; otherwise pinned to selection. */
  get focusId(): string | null {
    if (this.selectedNode?.id) return this.selectedNode.id;
    return this.hoverNodeId ?? this.highlightedNodeId ?? null;
  }

  get focusNeighbors(): Set<string> | null {
    const selection = this.selectionFocusIds;
    if (selection) return selection;
    const id = this.focusId;
    if (!id) return null;
    const cached = this.neighborFocusCache;
    if (cached?.id === id && cached.adjacency === this.adjacency) return cached.value;
    const value = new Set<string>([id, ...(this.adjacency.get(id) ?? [])]);
    this.neighborFocusCache = { id, adjacency: this.adjacency, value };
    return value;
  }

  /** Bright nodes when a node is selected: self + direct hierarchy neighbors (tree) or graph neighbors (network). */
  get selectionFocusIds(): Set<string> | null {
    const selectedId = this.selectedNode?.id;
    if (!selectedId) return null;

    const cached = this.selectionFocusCache;
    if (cached?.id === selectedId && cached.mode === this.viewMode && cached.tree === this.tree &&
        cached.nodes === this.filteredNodes && cached.adjacency === this.adjacency) return cached.value;

    const visibleIds = new Set(this.filteredNodes.map((node) => node.id));
    let focus: Set<string>;
    if (this.viewMode === 'tree' && this.tree) {
      focus = getHierarchySelectionFocusIds(this.tree, selectedId, visibleIds);
    } else {
      focus = new Set<string>([selectedId]);
      this.adjacency.get(selectedId)?.forEach((id) => {
        if (visibleIds.has(id)) focus.add(id);
      });
    }
    this.selectionFocusCache = { id: selectedId, mode: this.viewMode, tree: this.tree,
      nodes: this.filteredNodes, adjacency: this.adjacency, value: focus };
    return focus;
  }

  get shouldDimGraph(): boolean {
    return !!this.selectionFocusIds || (!!this.hoverNodeId && !this.selectedNode);
  }

  private isSelectionLinkActive(sourceId: string, targetId: string): boolean {
    const selectedId = this.selectedNode?.id;
    const focus = this.selectionFocusIds;
    if (!selectedId || !focus) return false;
    if (!focus.has(sourceId) || !focus.has(targetId)) return false;
    return sourceId === selectedId || targetId === selectedId;
  }

  get connectedNodes(): Array<{ label: string; node?: ViewerNode }> {
    if (!this.selectedNode) return [];
    return this.graphData.edges
      .filter((e) => e.source === this.selectedNode!.id || e.target === this.selectedNode!.id)
      .map((e) => {
        const otherId = e.source === this.selectedNode!.id ? e.target : e.source;
        return {
          label: e.label,
          node: this.graphData.nodes.find((n) => n.id === otherId),
        };
      })
      .filter((x) => x.node);
  }

  get selectedNodeDetailPanel(): NodeDetailPanel | null {
    if (!this.selectedNode) return null;
    if (this.selectedDetailCache?.node !== this.selectedNode || this.selectedDetailCache.graph !== this.graphData) {
      this.selectedDetailCache = { node: this.selectedNode, graph: this.graphData,
        panel: buildNodeDetailPanel(this.selectedNode, this.graphData) };
    }
    return this.selectedDetailCache.panel;
  }

  get selectedOsintCard(): OsintStyleCard | null {
    if (!this.selectedNode) return null;
    this.selectedNodeDetailPanel;
    const cache = this.selectedDetailCache!;
    return cache.card ?? (cache.card = buildOsintStyleCard(this.selectedNode, this.graphData));
  }

  private static readonly INFO_TOOLTIP_SECTION_TITLES = new Set(['how this was found', 'leak source']);

  private isInfoTooltipSection(section: NodeDetailSection): boolean {
    return LinkAnalysisGraphDashboardComponent.INFO_TOOLTIP_SECTION_TITLES.has(section.title.toLowerCase());
  }

  get selectedNodeInfoSections(): NodeDetailSection[] {
    const panel = this.selectedNodeDetailPanel;
    if (!panel) return [];
    return panel.sections.filter((section) => this.isInfoTooltipSection(section));
  }

  get selectedNodeBodySections(): NodeDetailSection[] {
    const panel = this.selectedNodeDetailPanel;
    if (!panel) return [];
    return panel.sections.filter((section) => !this.isInfoTooltipSection(section));
  }

  get selectedNodeBodyStats(): NodeDetailStat[] {
    const panel = this.selectedNodeDetailPanel;
    if (!panel || !this.selectedNode) return [];

    const byLabel = new Map(panel.stats.map((stat) => [stat.label, stat]));
    const body: NodeDetailStat[] = [];

    // Existing overview cards first
    for (const label of ['Attribution', 'Source', 'Layer', 'Platform']) {
      const stat = byLabel.get(label);
      if (stat) body.push(stat);
    }

    // Header metrics also shown at end of this box (before Notes)
    const confidence = byLabel.get('Confidence');
    if (confidence) {
      body.push({ ...confidence, tone: confidence.tone || 'success' });
    } else if (this.selectedMeta) {
      body.push({
        label: 'Confidence',
        value: `${this.selectedMeta.confidence}%`,
        tone: 'success',
      });
    }

    const evidence = byLabel.get('Evidence') || byLabel.get('Tier');
    if (evidence) {
      body.push({ ...evidence, label: 'Tier', tone: evidence.tone || 'info' });
    }

    const degree = this.degreeMap[this.selectedNode.id] ?? 0;
    body.push({
      label: 'Connections',
      value: String(degree),
      tone: 'muted',
    });

    if (this.selectedMeta) {
      const risk = this.selectedMeta.risk;
      body.push({
        label: 'Risk',
        value: risk.charAt(0).toUpperCase() + risk.slice(1),
        tone: risk === 'high' ? 'danger' : risk === 'medium' ? 'warning' : 'success',
      });
    }

    return body;
  }

  get timelineTotalVisible(): number {
    return this.timelineVisibleIds ? this.timelineVisibleIds.size : this.graphData.nodes.length;
  }

  get selectedMeta(): { confidence: number; risk: 'low' | 'medium' | 'high' } | null {
    if (!this.selectedNode) return null;
    return deriveMeta(this.selectedNode, this.degreeMap[this.selectedNode.id] ?? 0);
  }

  get selectedBrand(): { color: string; short: string } | null {
    return getBrand(this.selectedNode ?? undefined);
  }

  get selectedColor(): string {
    if (!this.selectedNode) return '#888';
    return this.selectedBrand?.color ?? ENTITY_COLORS[this.selectedNode.type];
  }

  get treePanelRightOffset(): number {
    return this.isPanelOpen ? this.effectiveDetailsPanelWidth + 12 : 12;
  }

  get effectiveDetailsPanelWidth(): number {
    return this.isPanelCollapsed ? 72 : this.detailsPanelWidth;
  }

  hasNodeImage(_node: ViewerNode | null | undefined): boolean {
    return false;
  }

  getNodePlatformKey(node: ViewerNode | null | undefined): string | null {
    if (!node) return null;
    const raw = node.raw || {};
    const hint =
      raw.metadata?.platform ||
      raw.metadata?.sourceName ||
      raw.metadata?.sourcePlatform ||
      node.details?.['platform'] ||
      node.label;
    const cleaned = String(hint || '')
      .replace(/^osint\s*source:\s*/i, '')
      .trim();
    return cleaned || null;
  }

  getNodeBrandIconUrl(node: ViewerNode | null | undefined): string | null {
    if (!node || !this.isPlatformBrandNode(node)) return null;
    const platform = this.getNodePlatformKey(node);
    if (!platform) return null;
    return createGraphPlatformIconDataUrl(platform) || null;
  }

  getNodeBrandColor(node: ViewerNode | null | undefined): string | null {
    if (!node || !this.isPlatformBrandNode(node)) return null;
    const platform = this.getNodePlatformKey(node);
    if (!platform) return null;
    return getSimpleIconBrandColor(platform) || resolvePlatformIconVisual(platform).iconBg || null;
  }

  private isPlatformBrandNode(node: ViewerNode): boolean {
    const raw = node.raw || {};
    if (raw.metadata?.isManualNode || raw.sourceType === 'manual') return true;
    if (isIdentityHubNode(node) || isAggregateHubNode(raw) || isSourceTypeHubNode(raw) || raw.metadata?.isHint) {
      return false;
    }
    const rawType = String(raw.type || '').toLowerCase();
    if (
      [
        'person',
        'email',
        'email_hint',
        'phone',
        'phone_hint',
        'starter',
        'image',
        'face_result',
        'location',
        'address',
        'aggregate_phones',
        'aggregate_emails',
        'aggregate_names',
        'aggregate_photos',
        'query_root',
        'face_image',
        'image_query',
      ].includes(rawType)
    ) {
      return false;
    }
    return (
      ['osint_source', 'social_profile', 'leak_source', 'website', 'social', 'username'].includes(rawType) ||
      node.type === 'social'
    );
  }

  onNodeImageError(nodeId: string): void {
    this.failedNodeImages.add(nodeId);
  }

  onDetailImageError(url: string): void {
    this.failedDetailImages.add(url);
  }

  getOsintCardThemeStyle(card: OsintStyleCard | null): Record<string, string> {
    const accent = card?.accent || '#38bdf8';
    const hex = accent.replace('#', '');
    const normalizedHex =
      hex.length === 3
        ? hex
          .split('')
          .map((character) => character + character)
          .join('')
        : hex;
    const colorValue = Number.parseInt(normalizedHex, 16);
    const isValidHex = normalizedHex.length === 6 && !Number.isNaN(colorValue);
    const red = isValidHex ? (colorValue >> 16) & 255 : 56;
    const green = isValidHex ? (colorValue >> 8) & 255 : 189;
    const blue = isValidHex ? colorValue & 255 : 248;
    return {
      '--source-accent': isValidHex ? accent : '#38bdf8',
      '--source-accent-rgb': `${red} ${green} ${blue}`,
    };
  }

  isPhotosHubCard(card: OsintStyleCard | null): boolean {
    return isPhotoHubCard(this.selectedNode, card);
  }

  get photosHubTiles(): PhotosHubTile[] {
    if (!this.isPhotosHubCard(this.selectedOsintCard)) return [];

    const toTile = (image: SearchImage, nodeId?: string): PhotosHubTile => {
      const platform = this.photoPlatformLabel(image);
      return {
        image: {
          ...image,
          platform,
          name: platform || image.name,
        },
        nodeId,
      };
    };

    const collectedTiles = uniqueOsintPhotoTiles(
      getAggregateItems(this.selectedNode?.raw || this.selectedNode).map((item) => {
        const url = String(item?.value || '').trim();
        if (!/^https?:\/\//i.test(url) && !isAuthGatedOsintImageUrl(url) && !url.startsWith('data:image/')) {
          return null;
        }
        const sourceName = this.resolvePhotoSourceName(url, item.sources);
        const image = this.findGalleryImageForUrl(url) || this.asPassthroughImage(url, sourceName);
        if (!image?.src) return null;
        return {
          ...image,
          platform: sourceName || this.photoPlatformLabel(image),
          name: sourceName || image.name,
        };
      }),
    ).map((image) => toTile(image, this.findRelatedPhotoNodeId(image)));
    if (collectedTiles.length) return collectedTiles;

    const uniqueGallery = uniqueOsintPhotoTiles(this.osintGalleryImages);
    const snapshotKeys = this.collectSnapshotOsintImageKeys();
    const scopedGallery =
      snapshotKeys.length > 0
        ? uniqueGallery.filter((image) => {
          const key = osintImageDedupeKey(image.detectUrl) || osintImageDedupeKey(image.src);
          return !!key && snapshotKeys.some((snapshot) => snapshot === key || snapshot.includes(key) || key.includes(snapshot));
        })
        : uniqueGallery;
    const galleryTiles = (scopedGallery.length ? scopedGallery : uniqueGallery).map((image) =>
      toTile(image, this.findRelatedPhotoNodeId(image)),
    );
    if (galleryTiles.length) return galleryTiles;

    const relatedTiles = uniqueOsintPhotoTiles(
      (this.selectedOsintCard?.relatedItems || []).map((item) => {
        const image =
          this.findGalleryImageForUrl(item.image) || this.asPassthroughImage(item.image, item.label);
        if (!image?.src) return null;
        return {
          ...image,
          platform: this.photoPlatformLabel(image, item.subtitle),
        };
      }),
    ).map((image) => {
      const nodeId = (this.selectedOsintCard?.relatedItems || []).find(
        (item) =>
          item.image &&
          (this.urlsReferToSameOsintImage(item.image, image.detectUrl) || item.image === image.src),
      )?.id;
      return toTile(image, nodeId);
    });

    return relatedTiles;
  }

  get photosHubSubject(): string {
    const root = this.graphData.nodes.find((node) => node.id === this.rootId);
    const type = String(root?.type || root?.raw?.type || '').toLowerCase();
    if (type === 'phone') return 'number';
    if (type === 'email') return 'email';
    if (type === 'username' || type === 'social') return 'username';
    if (type === 'person') return 'name';
    return 'search';
  }

  shouldShowOsintPictures(card: OsintStyleCard | null): boolean {
    if (!card || this.isPhotosHubCard(card)) return false;
    // Show recovered photos on leak sources, but never turn breach logos into photos.
    if (card.kind === 'leak') return this.getSourceCardPictures(card).length > 0;
    // Face card renders its own full-width preview image
    if (card.kind === 'face') return false;
    if (card.kind === 'identity-hub') {
      return card.placeholderIcon === 'bi bi-image';
    }
    // Never show an empty Pictures frame — only when this card has a displayable image
    // (gallery blob/data, public http(s), or OSINT proxy path pending gallery).
    const picture = this.getOsintCardPicture(card);
    return this.isDisplayablePictureSrc(picture?.src);
  }

  formatFaceMatchPercent(card: OsintStyleCard | null): string {
    const probability = card?.matchProbability;
    if (probability == null || !Number.isFinite(probability)) return 'N/A';
    return `${(probability * 100).toFixed(2)}%`;
  }

  isHighFaceMatch(card: OsintStyleCard | null): boolean {
    const probability = card?.matchProbability;
    if (probability == null || !Number.isFinite(probability)) return false;
    return probability >= 0.94;
  }

  getFaceCardImageSrc(card: OsintStyleCard | null): string | null {
    if (!card || this.faceCardImageUnavailable) return null;
    const candidates = [
      ...(card.faceImageCandidates || []),
      card.heroImage?.url,
    ].filter(
      (url): url is string =>
        typeof url === 'string' && this.isDisplayablePictureSrc(url.trim()),
    );
    for (const url of candidates) {
      const trimmed = url.trim();
      if (this.failedFaceCardImages.has(trimmed)) continue;
      // Prefer gallery/proxy when available, otherwise use the raw Face1 / match URL.
      return this.displayImageUrl(trimmed) || (isAuthGatedOsintImageUrl(trimmed) ? null : trimmed);
    }
    return null;
  }

  onFaceCardImageError(card: OsintStyleCard | null, event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img || !card) return;
    const failed = String(img.currentSrc || img.src || '').trim();
    if (failed) this.failedFaceCardImages.add(failed);

    const candidates = [
      ...(card.faceImageCandidates || []),
      card.heroImage?.url,
    ]
      .map((url) => String(url || '').trim())
      .filter(
        (url) =>
          this.isDisplayablePictureSrc(url) &&
          !this.failedFaceCardImages.has(url) &&
          !isAuthGatedOsintImageUrl(url),
      );

    const next = candidates[0];
    if (next) {
      img.src = this.displayImageUrl(next) || next;
      return;
    }
    // Show framed placeholder instead of an empty dark gap
    this.faceCardImageUnavailable = true;
    this.cdr.markForCheck();
  }

  onFaceCardImageSearch(card: OsintStyleCard | null, event?: Event): void {
    const src = this.getFaceCardImageSrc(card);
    if (!src) return;
    this.startImageSearchFromSrc(src, event);
  }

  /** Same gallery lookup OSINT phone / email / username cards use. */
  getPlatformImage(platform: string): SearchImage | undefined {
    const normalizedPlatform = this.normalizePlatformNameForLookup(platform);
    if (!normalizedPlatform) return undefined;

    const fromGallery = this.osintGalleryImages.find(
      (image) =>
        this.normalizePlatformNameForLookup(image.platform) === normalizedPlatform ||
        this.normalizePlatformNameForLookup(image.name) === normalizedPlatform,
    );
    if (fromGallery?.src) return fromGallery;

    const urls = this.platformImageUrls.get(normalizedPlatform) || [];
    const fromMappedUrl = this.osintGalleryImages.find((image) =>
      urls.some((url) => this.urlsReferToSameOsintImage(image.detectUrl, url)),
    );
    if (fromMappedUrl?.src) return fromMappedUrl;

    return this.osintGalleryImages.find((image) => {
      const detect = String(image.detectUrl || '').toLowerCase();
      return (
        detect.endsWith(`_${normalizedPlatform}`) ||
        detect.includes(`_${normalizedPlatform}.`) ||
        detect.includes(`_${normalizedPlatform}?`)
      );
    });
  }

  getOsintCardPicture(card: OsintStyleCard | null): SearchImage | undefined {
    if (!card) return undefined;
    const pictures = this.getSourceCardPictures(card);
    const selected = this.sourcePictureSelections.get(this.selectedNode?.id || '');
    const selectedPicture = selected && pictures.find((picture) => this.trackSourcePhoto(0, picture) === selected);
    if (selectedPicture) return selectedPicture;
    if (pictures.length) return pictures[0];

    // Only show a picture that belongs to this card/node — never borrow a
    // gallery image just because another result shares the same platform name.
    if (card.heroImage?.url) {
      const fromHero =
        this.findGalleryImageForUrl(card.heroImage.url) ||
        this.asPassthroughImage(card.heroImage.url, card.title);
      if (fromHero?.src) return fromHero;
    }

    for (const item of card.relatedItems || []) {
      const related = this.getRelatedItemPicture(item);
      if (related?.src) return related;
    }

    return undefined;
  }

  private getSourceCardPictures(card: OsintStyleCard | null): SearchImage[] {
    if (!card || card.kind === 'face' || this.isPhotosHubCard(card)) return [];
    const cache = this.sourcePicturesCache;
    if (cache?.card === card && cache.gallery === this.osintGalleryImages && cache.failures === this.failedDetailImages.size) {
      return cache.pictures;
    }
    const images: Array<{ url: string; label?: string } | null | undefined> = card.kind === 'leak'
      ? (this.selectedNode?.raw?.metadata?.collectedPhotos || []).map((photo: any) => ({ url: photo.url, label: photo.label }))
      : [card.heroImage, ...(this.selectedNodeDetailPanel?.images || [])];
    const seen = new Set<string>();
    const pictures = images.map((image) => {
      if (!image?.url) return undefined;
      const exact = this.osintGalleryImages.find((picture) => picture.detectUrl === image.url || picture.src === image.url);
      return exact || (isAuthGatedOsintImageUrl(image.url) ? this.findGalleryImageForUrl(image.url) : undefined) ||
        this.asPassthroughImage(image.url, image.label || card.title);
    }).filter((image): image is SearchImage => {
      if (!image?.src) return false;
      const key = this.trackSourcePhoto(0, image);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    this.sourcePicturesCache = { card, gallery: this.osintGalleryImages, failures: this.failedDetailImages.size, pictures };
    return pictures;
  }

  get sourcePhotoThumbnails(): SearchImage[] {
    const card = this.selectedOsintCard;
    const primary = this.getOsintCardPicture(card);
    return this.getSourceCardPictures(card).filter((image) => !primary ||
      this.trackSourcePhoto(0, image) !== this.trackSourcePhoto(0, primary));
  }

  trackSourcePhoto(_index: number, image: SearchImage): string {
    const url = image.detectUrl || image.src;
    return isAuthGatedOsintImageUrl(url) ? osintImageDedupeKey(url) : url;
  }

  getSourcePhotoPosition(card: OsintStyleCard): number {
    const pictures = this.getSourceCardPictures(card);
    const primary = this.getOsintCardPicture(card);
    return Math.max(0, pictures.findIndex(image => primary &&
      this.trackSourcePhoto(0, image) === this.trackSourcePhoto(0, primary))) + 1;
  }

  cycleSourcePhoto(card: OsintStyleCard, direction: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.selectedNode) return;
    const pictures = this.getSourceCardPictures(card);
    if (pictures.length < 2) return;
    const current = this.getSourcePhotoPosition(card) - 1;
    const next = pictures[(current + direction + pictures.length) % pictures.length];
    this.resetSourcePictureFallback();
    this.sourcePictureSelections.set(this.selectedNode.id, this.trackSourcePhoto(0, next));
    this.cdr.markForCheck();
  }

  private resetSourcePictureFallback(): void {
    const frame = this.sourcePictureLink?.nativeElement;
    frame?.classList.remove('is-fallback');
    const image = frame?.querySelector('img');
    if (image) {
      delete image.dataset['avatarFallback'];
      image.classList.remove('is-fallback');
      image.style.removeProperty('display');
    }
  }

  selectSourcePhoto(image: SearchImage, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.selectedNode) return;
    this.resetSourcePictureFallback();
    this.sourcePictureSelections.set(this.selectedNode.id, this.trackSourcePhoto(0, image));
    this.cdr.detectChanges();
    this.sourcePictureLink?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.sourcePictureLink?.nativeElement.focus({ preventScroll: true });
  }

  getRelatedItemPicture(item: OsintStyleRelatedItem | null | undefined): SearchImage | undefined {
    if (!item?.image) return undefined;
    return this.findGalleryImageForUrl(item.image) || this.asPassthroughImage(item.image, item.label);
  }

  displayImageUrl(url?: string | null): string | null {
    if (!url) return null;
    const gallery = this.findGalleryImageForUrl(url);
    if (gallery?.src) return gallery.src;
    // Auth-gated proxy must use gallery blob/data from extractImagesFromSources — never raw.
    if (isAuthGatedOsintImageUrl(url) || this.failedDetailImages.has(url)) return null;
    if (!this.isDisplayablePictureSrc(url)) return null;
    return url;
  }

  displayNodeImage(node: ViewerNode | null | undefined): string | null {
    if (!node) return null;
    const metadata = node.raw?.metadata || {};
    const candidates = [
      node.image,
      node.raw?.imageUrl,
      node.raw?.image_url,
      metadata.imageUrl,
      metadata.image_url,
      metadata.queryValue,
      metadata.relatedImageUrl,
      ...(Array.isArray(metadata.searchableValues?.image) ? metadata.searchableValues.image : []),
      ...(Array.isArray(metadata.searchable?.image) ? metadata.searchable.image : []),
    ];
    for (const candidate of candidates) {
      const fromUrl = this.displayImageUrl(typeof candidate === 'string' ? candidate : null);
      if (fromUrl) return fromUrl;
    }
    const platform = metadata.platform || metadata.sourceName;
    return this.getPlatformImage(platform)?.src || null;
  }

  private getGraphNodePhotoUrl(node: ViewerNode | null | undefined): string | null {
    if (!node) return null;
    if (node.raw?.metadata?.isManualNode || node.raw?.sourceType === 'manual') {
      const suppliedImage = node.raw?.imageUrl || node.raw?.metadata?.imageUrl || node.image;
      if (suppliedImage) return this.displayImageUrl(suppliedImage);
    }
    if (isAggregateHubNode(node.raw || node)) return null;
    if (this.isPlatformBrandNode(node)) {
      for (const url of this.getServiceNodePresentation(node).images) {
        const display = this.displayImageUrl(url);
        if (display) return display;
      }
      return null;
    }
    const rawType = String(node.raw?.type || node.type || '').toLowerCase();
    if (
      node.type !== 'image' &&
      node.type !== 'person' &&
      rawType !== 'face_image' &&
      rawType !== 'image' &&
      rawType !== 'face_result'
    ) {
      return null;
    }
    return this.displayNodeImage(node);
  }

  private getServiceNodePresentation(node: ViewerNode): { value: string; images: string[] } {
    const key = node.raw ?? node;
    let presentation = this.serviceNodePresentation.get(key);
    if (!presentation) {
      presentation = { value: serviceNodePrimaryValue(node), images: collectNodeDetailImages(node).map(image => image.url) };
      this.serviceNodePresentation.set(key, presentation);
    }
    return presentation;
  }

  /** True when src can be shown in an <img> (gallery blob/data, http(s), or OSINT proxy). */
  private isDisplayablePictureSrc(src?: string | null): boolean {
    const value = String(src || '').trim();
    if (!value) return false;
    return (
      /^https?:\/\//i.test(value) ||
      value.startsWith('data:image/') ||
      value.startsWith('blob:') ||
      isAuthGatedOsintImageUrl(value)
    );
  }

  onOsintPictureError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img) return;
    if (img.dataset['avatarFallback'] === '1') {
      img.style.display = 'none';
      return;
    }
    img.dataset['avatarFallback'] = '1';
    img.classList.add('is-fallback');
    img.closest('.la-osint-picture-frame')?.classList.add('is-fallback');
    img.closest('.la-osint-related-thumb')?.classList.add('is-fallback');
    img.closest('.la-osint-photos-item')?.classList.add('is-fallback');
    img.src = DEFAULT_PROFILE_AVATAR_SRC;
  }

  canStartImageSearch(image?: ImageSearchCandidate): boolean {
    return canStartImageSearchUtil(image);
  }

  canStartFaceSearchForCard(card: OsintStyleCard | null): boolean {
    const src = this.getFaceCardImageSrc(card);
    if (!src) return false;
    if (card?.kind === 'face') {
      return this.canStartImageSearch({ src, detectUrl: src, faceDetected: true });
    }
    return this.canStartImageSearch(this.findGalleryImageForUrl(src) || src);
  }

  startImageSearchFromSrc(imageOrSrc: ImageSearchCandidate | string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canStartImageSearch(imageOrSrc)) return;
    const src = resolveImageSearchSrc(imageOrSrc);
    if (!src) return;
    void navigateToOsintImageSearch(this.router, src).catch(() => {
      this.toastr.error('Unable to start Image Search');
    });
  }

  onPhotosHubTileClick(tile: PhotosHubTile): void {
    if (tile.nodeId) {
      this.selectNode(tile.nodeId);
      return;
    }
    const href = tile.image.detectUrl || tile.image.src;
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  }

  private collectSnapshotOsintImageKeys(): string[] {
    const keys = new Set<string>();
    const add = (value: unknown) => {
      if (Array.isArray(value)) {
        value.forEach(add);
        return;
      }
      const text = String(value || '').trim();
      if (!text) return;
      keys.add(osintImageDedupeKey(text) || text.toLowerCase());
    };
    const ingestNode = (node: any) => {
      const snapshot = node?.metadata?.normalizedResult || node?.raw?.metadata?.normalizedResult;
      if (!snapshot) return;
      add(snapshot.image_url);
      add(snapshot.image_hash);
    };
    (this.graphPayload?.nodes || []).forEach(ingestNode);
    this.graphData.nodes.forEach((node) => ingestNode(node.raw || node));
    return Array.from(keys).filter(Boolean);
  }

  private photoPlatformLabel(image: SearchImage, fallback?: string): string {
    for (const candidate of [image.platform, image.name, fallback]) {
      const value = String(candidate || '').trim();
      if (!value) continue;
      const normalized = value.toLowerCase().replace(/[_-]+/g, ' ');
      if (
        normalized === 'image' ||
        normalized === 'images' ||
        normalized === 'photo' ||
        normalized === 'photos' ||
        normalized === 'osint result image' ||
        normalized === 'osint result' ||
        normalized === 'osint_result' ||
        normalized === 'profile image' ||
        normalized === 'root phone node' ||
        normalized === 'root_phone_node' ||
        normalized === 'root email node' ||
        normalized === 'root_email_node'
      ) {
        continue;
      }
      if (normalized.includes('duolingo')) return 'Duolingo';
      if (normalized.includes('whatsapp')) return 'Whatsapp';
      if (normalized.includes('truecaller')) return 'Truecaller';
      return value
        .split(/[._\s-]+/)
        .filter((part) => part && !['com', 'www', 'ssl', 'simg', 'cdn', 'img'].includes(part.toLowerCase()))
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') || value;
    }
    return '';
  }

  private resolvePhotoSourceName(url: string, sources?: string[]): string {
    for (const source of sources || []) {
      const label = this.photoPlatformLabel({ platform: source, src: url, name: source, height: 0 });
      if (label) return label;
    }
    for (const node of this.graphData.nodes) {
      const raw = node.raw || node;
      if (isAggregateHubNode(raw)) continue;
      const metadata = raw.metadata || {};
      const images = [
        ...(Array.isArray(metadata.searchable?.image) ? metadata.searchable.image : []),
        ...(Array.isArray(metadata.searchableValues?.image) ? metadata.searchableValues.image : []),
        raw.imageUrl,
        metadata.imageUrl,
        node.image,
      ];
      const matches = images.some(
        (image) =>
          String(image || '').trim() &&
          (String(image) === url || this.urlsReferToSameOsintImage(String(image), url)),
      );
      if (!matches) continue;
      const label = this.photoPlatformLabel({
        platform: metadata.platform || metadata.sourceName || node.label,
        src: url,
        name: node.label,
        height: 0,
      });
      if (label) return label;
    }
    return '';
  }

  private findRelatedPhotoNodeId(image: SearchImage): string | undefined {
    const card = this.selectedOsintCard;
    const match = (card?.relatedItems || []).find((item) => {
      if (!item.image) return false;
      return this.urlsReferToSameOsintImage(item.image, image.detectUrl) || item.image === image.src;
    });
    return match?.id;
  }

  private normalizePlatformNameForLookup(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  private asPassthroughImage(url?: string | null, name = 'Image'): SearchImage | undefined {
    if (!url || this.failedDetailImages.has(url)) return undefined;
    // Auth-gated proxy must come from gallery (Bearer blob/data) — never use raw as <img> src.
    if (isAuthGatedOsintImageUrl(url)) return undefined;
    if (!(url.startsWith('http') || url.startsWith('data:image/'))) return undefined;
    return {
      platform: name,
      src: url,
      name,
      height: 150,
      detectUrl: url,
    };
  }

  private findGalleryImageForUrl(url?: string | null): SearchImage | undefined {
    if (!url) return undefined;
    if (this.galleryLookupCache?.gallery !== this.osintGalleryImages) {
      this.galleryLookupCache = { gallery: this.osintGalleryImages, urls: new Map() };
    }
    const urls = this.galleryLookupCache.urls;
    if (urls.has(url)) return urls.get(url);
    const image = this.osintGalleryImages.find(
      (image) =>
        image.src === url ||
        image.detectUrl === url ||
        this.urlsReferToSameOsintImage(image.detectUrl, url),
    );
    urls.set(url, image);
    return image;
  }

  private urlsReferToSameOsintImage(left?: string | null, right?: string | null): boolean {
    if (!left || !right) return false;
    if (left === right) return true;
    const hashOf = (value: string): string => {
      const proxy = value.match(/\/api\/osint\/image\/([^/?#]+)/i)?.[1];
      if (proxy) return proxy.toLowerCase();
      const file = value.split('?')[0].split('/').pop() || '';
      return file.toLowerCase();
    };
    const leftHash = hashOf(left);
    const rightHash = hashOf(right);
    return !!leftHash && !!rightHash && (leftHash === rightHash || leftHash.includes(rightHash) || rightHash.includes(leftHash));
  }

  onRelatedIdentityClick(itemId: string): void {
    if (!itemId) return;
    this.selectNode(itemId);
  }

  getNodeColor(node: ViewerNode): string {
    if (isIdentityHubNode(node)) {
      return IDENTITY_HUB_ACCENTS[resolveIdentityHubType(node)] || ENTITY_COLORS[node.type];
    }
    const rawType = String(node.raw?.type || '').toLowerCase();
    if (rawType === 'email_hint' || node.raw?.metadata?.isHint) {
      return IDENTITY_HUB_ACCENTS['email_hint'];
    }
    return getBrand(node)?.color ?? ENTITY_COLORS[node.type];
  }

  /** Avatar ring color: prefer entity type (e.g. social purple), not label shape. */
  getNodeAvatarColor(node: ViewerNode): string {
    if (isIdentityHubNode(node)) {
      return IDENTITY_HUB_ACCENTS[resolveIdentityHubType(node)] || this.entityColors[node.type];
    }
    const rawType = String(node.raw?.type || '').toLowerCase();
    if (rawType === 'email_hint' || node.raw?.metadata?.isHint) {
      return IDENTITY_HUB_ACCENTS['email_hint'];
    }
    if (node.type === 'phone' || node.type === 'email') {
      return this.entityColors[node.type];
    }
    if (node.type === 'social' || node.type === 'person' || node.type === 'location' || node.type === 'image') {
      return this.entityColors[node.type];
    }
    const fromLabel = this.getDetailValueEntityType(node.label);
    if (fromLabel === 'phone' || fromLabel === 'email') {
      return this.entityColors[fromLabel];
    }
    return this.getNodeBrandColor(node) || this.getNodeColor(node);
  }

  getNodeInitials(label: string): string {
    const parts = label.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    }
    return label.trim().slice(0, 2).toUpperCase() || '?';
  }

  onDetailsResizeStart(event: MouseEvent): void {
    if (this.isPanelCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizingDetailsPanel = true;
    this.detailsResizeStartX = event.clientX;
    const renderedWidth = this.detailsPanel?.nativeElement?.getBoundingClientRect().width;
    this.detailsResizeStartWidth =
      renderedWidth && renderedWidth > 0 ? renderedWidth : this.detailsPanelWidth;
    this.detailsPanelWidth = this.detailsResizeStartWidth;
  }

  toggleDetailsPanelCollapse(): void {
    if (!this.selectedNode) return;
    this.isPanelOpen = true;
    this.isPanelCollapsed = !this.isPanelCollapsed;
    this.resizingDetailsPanel = false;
    this.scheduleLayoutRefresh();
    setTimeout(() => this.refreshGraphAfterPanelChange(), 0);
  }

  private getGraphStageWidth(): number {
    const stage = this.graphContainerEl?.parentElement;
    const width = stage?.clientWidth ?? this.graphContainerEl?.clientWidth ?? 0;
    if (width > 0) return width;
    if (typeof window !== 'undefined') {
      return Math.max(window.innerWidth - 280, 480);
    }
    return 1280;
  }

  private getResponsiveDetailsPanelWidth(): number {
    const containerWidth = this.getGraphStageWidth();
    const target = Math.round(containerWidth * this.detailsPanelDefaultShare);
    const defaultCap = Math.floor(containerWidth * this.detailsPanelDefaultMaxShare);
    return this.clampDetailsPanelWidth(Math.min(target, defaultCap));
  }

  private getDetailsPanelMinWidth(): number {
    const containerWidth = this.getGraphStageWidth();
    if (typeof window === 'undefined') return this.detailsPanelMinWidth;
    if (containerWidth <= 520) return Math.min(this.detailsPanelMinWidth, Math.round(containerWidth * 0.92));
    return this.detailsPanelMinWidth;
  }

  private getDetailsPanelResizeMax(): number {
    const containerWidth = this.getGraphStageWidth();
    return Math.max(
      this.getDetailsPanelMinWidth(),
      Math.floor(containerWidth * this.detailsPanelResizeMaxShare),
    );
  }

  private clampDetailsPanelWidth(width: number): number {
    return Math.min(this.getDetailsPanelResizeMax(), Math.max(this.getDetailsPanelMinWidth(), width));
  }

  private syncDetailsPanelWidth(): void {
    const next = this.clampDetailsPanelWidth(this.detailsPanelWidth);
    if (next === this.detailsPanelWidth) return;
    this.detailsPanelWidth = next;
  }

  get paletteActions(): PaletteAction[] {
    return [
      { id: 'insights', label: 'Open Investigation Insights', hint: 'panel', run: () => (this.insightsOpen = true) },
      { id: 'timeline', label: 'Open Discovery Timeline', hint: 'panel', run: () => (this.timelineOpen = true) },
      { id: 'correlate', label: 'Open Correlation Analysis', hint: 'panel', run: () => (this.correlationOpen = true) },
      { id: 'map', label: 'Open Geographic Map', hint: 'view', run: () => this.openMap() },
      { id: 'view-network', label: 'Switch to Network View', hint: 'view', run: () => this.setViewMode('network') },
      { id: 'view-tree', label: 'Switch to Hierarchy / Tree View', hint: 'view', run: () => this.setViewMode('tree') },
      { id: 'share', label: 'Copy Shareable Link', hint: 'action', run: () => this.shareView() },
      { id: 'fit', label: 'Fit Graph to View', hint: 'action', run: () => this.resetView() },
      { id: 'reset', label: 'Reset All Filters & Selection', hint: 'action', run: () => this.fullReset() },
    ];
  }

  onBack(): void {
    this.back.emit();
  }

  onRegenerate(): void {
    this.regenerate.emit();
  }

  onStopAutomation(): void {
    if (this.stoppingAutomation) return;
    this.stopAutomation.emit();
  }

  toggleLayer(type: EntityType): void {
    const next = new Set(this.visibleLayers);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    this.visibleLayers = next;
    if (this.viewMode === 'tree') this.treeLayoutDirty = true;
    this.recomputeFilteredData();
    this.pushGraphData();
    if (this.filteredNodes.length) {
      this.scheduleFocusRootView();
    }
  }

  isLayerVisible(type: EntityType): boolean {
    return this.visibleLayers.has(type);
  }

  setViewMode(mode: ViewMode): void {
    if (this.viewMode === mode) return;
    this.subtreeDragSession = null;
    const previousMode = this.viewMode;
    this.snapshotPositionsForMode(previousMode);
    this.viewMode = mode;
    if (mode === 'tree') {
      this.treeLayoutDirty = !this.treeNodePositions.size;
    } else if (previousMode === 'tree') {
      this.clearPinnedTreeNodes();
    }
    this.recomputeFilteredData();
    if (!this.graph || !this.isGraphDomMounted()) {
      void this.syncGraph();
      return;
    }
    this.applyViewModeDag();
    this.applyForces();
    this.pushGraphData();
    this.scheduleLayoutRefresh();
    this.initialViewportApplied = false;
    if (mode === 'tree') {
      setTimeout(() => this.focusRootNode(300), 80);
    } else {
      this.scheduleFocusRootView();
    }
  }

  toggleTreePanel(): void {
    this.treePanelOpen = !this.treePanelOpen;
    setTimeout(() => this.scheduleLayoutRefresh(), 320);
  }

  toggleEntityLayers(): void {
    this.entityLayersCollapsed = !this.entityLayersCollapsed;
  }

  toggleBranchLayersPanel(): void {
    this.branchLayersCollapsed = !this.branchLayersCollapsed;
  }

  toggleBranchLayer(branchId: string): void {
    if (this.viewMode !== 'tree' || !this.isCollapsibleHierarchyNode(branchId)) return;

    const next = new Set(this.expandedBranchIds);
    if (next.has(branchId)) next.delete(branchId);
    else next.add(branchId);
    this.expandedBranchIds = next;

    this.treeLayoutDirty = true;
    this.recomputeFilteredData();
    this.pushGraphData();
    this.scheduleLayoutRefresh();
    this.graph?.refresh?.();
    this.cdr.detectChanges();
  }

  isBranchExpanded(branchId: string): boolean {
    return this.expandedBranchIds.has(branchId);
  }

  getBranchLayerNumber(nodeId: string): number | null {
    return this.branchLayers.find((b) => b.id === nodeId)?.number ?? null;
  }

  getBranchLayerItem(nodeId: string): { id: string; label: string; number: number; color: string } | null {
    return this.branchLayers.find((b) => b.id === nodeId) ?? null;
  }

  toggleInsights(): void {
    this.insightsOpen = !this.insightsOpen;
  }

  toggleTimeline(): void {
    this.timelineOpen = !this.timelineOpen;
    if (!this.timelineOpen) {
      this.timelineVisibleIds = null;
      this.stopTimeline();
    }
    this.recomputeFilteredData();
    this.pushGraphData();
  }

  toggleAnimateLinks(): void {
    this.animateLinks = !this.animateLinks;
    this.graph?.autoPauseRedraw(!this.animateLinks);
  }

  toggleExport(): void {
    this.exportOpen = !this.exportOpen;
  }

  openCommandPalette(): void {
    this.cmdOpen = true;
    this.refreshCmdItems();
  }

  closeCommandPalette(): void {
    this.cmdOpen = false;
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    if (!query.trim()) {
      this.highlightedNodeId = null;
      this.graph?.refresh?.();
      return;
    }
    const q = query.toLowerCase();
    const match = this.graphData.nodes.find(
      (n) => n.label.toLowerCase().includes(q) || n.id.toLowerCase().includes(q),
    );
    if (match) this.selectNode(match.id);
    else {
      this.highlightedNodeId = null;
      this.graph?.refresh?.();
    }
  }

  activateNode(nodeId: string, openPanel = true): void {
    const node = this.graphData.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    this.closeSearchPopover();
    this.hoverNodeId = null;
    this.hoverTooltipLabel = null;
    this.selectedNode = node;
    this.highlightedNodeId = nodeId;
    this.failedDetailImages.clear();
    this.failedFaceCardImages.clear();
    this.faceCardImageUnavailable = false;
    this.infoPopoverOpen = false;
    this.clearInfoPopoverCloseTimer();
    if (this.detailFieldSearchFocus) {
      this.detailFieldSearchFocus = null;
      this.clearDetailFieldSearch.emit();
    }
    if (openPanel) {
      this.treePanelOpen = false;
      this.isPanelOpen = true;
      this.isPanelCollapsed = false;
      this.syncDetailsPanelWidth();
    }
    this.noteDraft = this.annotations.getAnnotation(nodeId).note ?? '';
    this.noteSaved = false;
    this.nodeSelect.emit(node.raw ?? node);
    this.graph?.refresh?.();
    this.refreshGraphAfterPanelChange();
    this.refreshSearchPopoverActions(nodeId);
  }

  selectNode(nodeId: string, openPanel = true): void {
    this.activateNode(nodeId, openPanel);
  }

  closePanel(): void {
    this.isPanelOpen = false;
    this.isPanelCollapsed = false;
    this.infoPopoverOpen = false;
    this.clearInfoPopoverCloseTimer();
    this.selectedNode = null;
    this.highlightedNodeId = null;
    this.closeSearchPopover();
    this.graph?.refresh?.();
    setTimeout(() => this.refreshGraphAfterPanelChange(), 0);
  }

  openInfoPopover(): void {
    this.clearInfoPopoverCloseTimer();
    if (!this.selectedNodeInfoSections.length) return;
    this.infoPopoverOpen = true;
  }

  scheduleCloseInfoPopover(): void {
    this.clearInfoPopoverCloseTimer();
    this.infoPopoverCloseTimer = setTimeout(() => {
      this.infoPopoverOpen = false;
      this.infoPopoverCloseTimer = null;
    }, 120);
  }

  private clearInfoPopoverCloseTimer(): void {
    if (!this.infoPopoverCloseTimer) return;
    clearTimeout(this.infoPopoverCloseTimer);
    this.infoPopoverCloseTimer = null;
  }

  setRoot(nodeId: string): void {
    this.rootId = nodeId;
    const node = this.graphData.nodes.find((n) => n.id === nodeId);
    this.toastr.info(node?.label ?? nodeId, 'Hierarchy re-rooted');
    this.refreshHierarchy();
    this.treeLayoutDirty = true;
    this.treeNodePositions.clear();
    this.treeCompactPositions.clear();
    this.junctionPositions.clear();
    this.treeZoomSpreadApplied = 1;
    this.recomputeFilteredData();
    this.initialViewportApplied = false;
    if (this.viewMode === 'tree') {
      this.reinitGraphLayout();
    } else {
      this.pushGraphData();
    }
    this.scheduleFocusRootView();
  }

  setHopFilter(hops: number | null): void {
    this.hopFilter = hops;
    if (this.viewMode === 'tree') this.treeLayoutDirty = true;
    this.recomputeFilteredData();
    this.ensureVisibleGraphData();
    this.pushGraphData();
    this.scheduleLayoutRefresh();
  }

  clearIsolation(): void {
    this.isolatedId = null;
    this.recomputeFilteredData();
    this.pushGraphData();
  }

  zoomIn(): void {
    if (!this.graph) {
      this.queueGraphSync();
      return;
    }
    const z = this.graph.zoom?.() ?? 1;
    if (!Number.isFinite(z)) return;
    this.ngZone.runOutsideAngular(() => {
      this.graph.zoom(z * 1.4, 300);
      this.graph.refresh?.();
    });
  }

  zoomOut(): void {
    if (!this.graph) {
      this.queueGraphSync();
      return;
    }
    const z = this.graph.zoom?.() ?? 1;
    if (!Number.isFinite(z)) return;
    this.ngZone.runOutsideAngular(() => {
      this.graph.zoom(z / 1.4, 300);
      this.graph.refresh?.();
    });
  }

  resetView(): void {
    this.fitGraphToView(500);
  }

  fullReset(): void {
    this.selectedNode = null;
    this.highlightedNodeId = null;
    this.isPanelOpen = false;
    this.isolatedId = null;
    this.hoverNodeId = null;
    this.hoverTooltipLabel = null;
    this.correlationHighlight = new Set();
    this.timelineVisibleIds = null;
    this.hopFilter = null;
    this.searchQuery = '';
    this.searchPopover = null;
    if (this.detailFieldSearchFocus) {
      this.detailFieldSearchFocus = null;
      this.clearDetailFieldSearch.emit();
    }
    this.stopSearchPopoverDrag();
    this.initialViewportApplied = false;
    this.recomputeFilteredData();
    this.pushGraphData();
    setTimeout(() => this.focusRootNode(500), 50);
  }

  shareView(): void {
    if (typeof window === 'undefined') return;
    navigator.clipboard?.writeText?.(window.location.href);
    this.toastr.success('Current view saved in the URL', 'Shareable link copied');
  }

  onNodeAction(action: NodeActionItem): void {
    this.runAction.emit(action);
  }

  getDetailFieldSearchId(field: NodeDetailField): string {
    return `${field.label}::${field.searchQueryType || field.jumpToNodeId || ''}::${field.value}`;
  }

  isAggregateJumpField(field: NodeDetailField | null | undefined): boolean {
    return Boolean(field?.jumpToNodeId && field?.value);
  }

  onAggregateEntryJump(field: NodeDetailField, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (event && 'stopImmediatePropagation' in event) {
      (event as any).stopImmediatePropagation();
    }
    const sourceNodeId = String(field?.jumpToNodeId || '').trim();
    const value = String(field?.value || '').trim();
    if (!sourceNodeId || !value) return;

    this.jumpToSourceValue.emit({
      sourceNodeId,
      value,
      valueType: field.preselectValueType,
    });
  }

  isDetailFieldSearchable(field: NodeDetailField | null | undefined): boolean {
    if (this.isAggregateJumpField(field)) return false;
    if (!field?.searchQueryType) return false;
    const queryValue = resolveDetailFieldSearchValue(field);
    if (!queryValue) return false;
    if (!this.getDetailFieldSearchActions(field).length) return false;
    if (field.searchQueryType === 'image') {
      return this.canStartImageSearch(this.findGalleryImageForUrl(queryValue) || queryValue);
    }
    return true;
  }

  getDetailFieldSearchActions(field: NodeDetailField) {
    if (!field.searchQueryType) return [];
    const node = this.selectedNode?.raw || this.selectedNode;
    const allowed = node
      ? getServerActionsForValueType(node, field.searchQueryType)
      : null;
    return buildDetailFieldSearchActions(field.searchQueryType, allowed);
  }

  /** Open the node action popover for a detail-card searchable field (do not auto-run). */
  startDetailFieldSearch(field: NodeDetailField, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (event && 'stopImmediatePropagation' in event) {
      (event as any).stopImmediatePropagation();
    }
    if (this.isAggregateJumpField(field) || !this.isDetailFieldSearchable(field)) {
      this.toastr.error('This field does not contain a searchable value.');
      return;
    }

    const queryType = field.searchQueryType;
    const queryValue = resolveDetailFieldSearchValue(field);
    const sourceNodeId = String(this.selectedNode?.id || this.selectedNode?.raw?.id || '').trim();
    if (!queryType || !queryValue || !sourceNodeId) {
      this.toastr.error('No search actions available for this value.');
      return;
    }

    this.detailFieldSearchFocus = { queryType, queryValue };
    this.prepareDetailFieldSearch.emit({
      queryValue,
      queryType,
      sourceNodeId,
    });

    const mouse = event as MouseEvent | undefined;
    const anchor =
      mouse && Number.isFinite(mouse.clientX) && Number.isFinite(mouse.clientY)
        ? { x: mouse.clientX, y: mouse.clientY }
        : undefined;
    this.openSearchPopover(sourceNodeId, anchor);
    this.cdr.markForCheck();
  }

  isDetailFieldSearchFocused(field: NodeDetailField | null | undefined): boolean {
    if (!this.detailFieldSearchFocus || !field?.searchQueryType) return false;
    const queryValue = resolveDetailFieldSearchValue(field);
    return (
      this.detailFieldSearchFocus.queryType === field.searchQueryType &&
      this.detailFieldSearchFocus.queryValue === queryValue
    );
  }

  /** Actions shown in the node-search popover (optionally scoped to a detail field). */
  get popoverNodeActions(): NodeActionItem[] {
    const actions = [...this.getPopoverActions()];
    const sourceNodeId = this.searchPopover?.nodeId || this.selectedNode?.id;
    // Previously performed actions remain visible as read-only history even when
    // subscription or service restrictions remove them from availableActions.
    for (const previous of this.investigation?.action_history || []) {
      if (previous.status !== 'completed' || previous.source_node_id !== sourceNodeId) continue;
      if (this.detailFieldSearchFocus && previous.query_type !== this.detailFieldSearchFocus.queryType) continue;
      const kind = ({osint_search:'osint',leak_search:'leak',face_search:'face'} as Record<string,string>)[previous.action_type];
      if (!kind || !['phone','email','username','name','image'].includes(previous.query_type)) continue;
      const key = `${previous.query_type}-${kind}`;
      if (!actions.some(action => action.key === key)) {
        actions.push({key, label:`Run ${kind === 'osint' ? 'OSINT' : kind} ${previous.query_type} search`,
          actionType:previous.action_type,queryType:previous.query_type,disabled:true});
      }
    }
    return actions.map(action => {
      const node = this.searchPopover?.node || this.selectedNode;
      const options = this.valueOptions.filter(option => option.type === action.queryType);
      const values = options.length
        ? options.filter(option => !this.selectedSearchValues.length || this.selectedSearchValues.includes(option.value)).map(option => option.value)
        : ((getNodeSearchableValues(node?.raw || node) as any)[action.queryType] || []);
      const completedValues = values.filter((value: string) => isSearchCompleted(this.investigation?.action_history || [], {
        source_node_id: this.searchPopover?.nodeId || node?.id || '', action_type: action.actionType,
        query_type: action.queryType, query_value: value, image_url: action.queryType === 'image' ? value : undefined,
      }));
      const completed = values.length > 0 && completedValues.length === values.length;
      return {...action, completed, completedValues, disabled: action.disabled || completed};
    });
  }

  private getPopoverActions(): NodeActionItem[] {
    if (!this.detailFieldSearchFocus) return this.nodeActions;
    const queryType = this.detailFieldSearchFocus.queryType;
    const allowed = buildDetailFieldSearchActions(
      queryType,
      this.selectedNode?.raw || this.selectedNode
        ? getServerActionsForValueType(this.selectedNode.raw || this.selectedNode, queryType)
        : null,
    );
    const allowedKeys = new Set(allowed.map((action) => action.key));
    const filtered = this.nodeActions.filter(
      (action) => action.queryType === queryType && allowedKeys.has(action.key),
    );
    if (filtered.length) return filtered;
    return allowed.map((action) => ({
      key: action.key,
      label: action.label,
      actionType: action.actionType,
      queryType: action.queryType,
    }));
  }

  onActionKeySelectionChange(key: string, event: Event): void {
    event.stopPropagation();
    if (this.popoverNodeActions.some(action => action.key === key && (action.disabled || action.running))) return;
    const input = event.target as HTMLInputElement | null;
    this.actionKeySelect.emit({ key, selected: Boolean(input?.checked) });
  }

  isActionKeySelected(key: string): boolean {
    return this.selectedActionKeys.includes(key);
  }

  trackNodeAction(_index: number, action: NodeActionItem): string {
    return action.key;
  }

  onRunSelectedAction(): void {
    this.runSelectedAction.emit();
  }

  /** Popover node is an aggregate hub (Names / Emails / Phones). */
  get isAggregatePopover(): boolean {
    const node = this.searchPopover?.node;
    if (!node) return false;
    return isAggregateHubNode(node.raw || node);
  }

  /** View-only unique list for aggregate popovers (no SELECT VALUE checkboxes). */
  get aggregateViewItems(): Array<{
    value: string;
    label: string;
    sourceNodeId?: string;
    valueType?: string;
  }> {
    if (!this.isAggregatePopover || !this.searchPopover?.node) return [];
    const raw = this.searchPopover.node.raw || this.searchPopover.node;
    const items = getAggregateItems(raw);
    const collectionType = String(
      raw?.metadata?.collectionType ||
        (raw?.type === 'aggregate_phones'
          ? 'phone'
          : raw?.type === 'aggregate_emails'
            ? 'email'
            : raw?.type === 'aggregate_names'
              ? 'name'
              : raw?.type === 'aggregate_photos'
                ? 'image'
                : 'value'),
    );
    const valueType = collectionType === 'photo' ? 'image' : collectionType;
    return items.map((item) => {
      const sources = (item.sources || []).map((src) => String(src).trim()).filter(Boolean);
      const sourceText = sources.length
        ? sources.join(', ')
        : String(item.sourceLabel || '').trim();
      return {
        value: item.value,
        label: sourceText ? `${item.value} (${sourceText})` : item.value,
        ...(item.sourceNodeId ? { sourceNodeId: item.sourceNodeId } : {}),
        valueType,
      };
    });
  }

  get aggregateViewSectionLabel(): string {
    const raw = this.searchPopover?.node?.raw || this.searchPopover?.node;
    const type = String(raw?.metadata?.collectionType || raw?.type || '').toLowerCase();
    if (type.includes('phone')) return 'Collected phones';
    if (type.includes('email')) return 'Collected emails';
    if (type.includes('name')) return 'Collected names';
    if (type.includes('photo') || type.includes('image')) return 'Collected photos';
    return 'Collected values';
  }

  onAggregatePopoverItemClick(
    item: { value: string; sourceNodeId?: string; valueType?: string },
    event: Event,
  ): void {
    event.stopPropagation();
    const sourceNodeId = String(item?.sourceNodeId || '').trim();
    const value = String(item?.value || '').trim();
    if (!sourceNodeId || !value) return;
    this.jumpToSourceValue.emit({
      sourceNodeId,
      value,
      valueType: item.valueType,
    });
  }

  trackAggregateViewItem(_index: number, item: { value: string; label: string }): string {
    return item.value;
  }

  get valueOptions(): Array<{ value: string; label: string; type: string }> {
    // Aggregates never use the SELECT VALUE checkbox picker.
    if (this.isAggregatePopover) return [];
    if (this.detailFieldSearchFocus) {
      return [
        {
          value: this.detailFieldSearchFocus.queryValue,
          label: this.detailFieldSearchFocus.queryValue,
          type: this.detailFieldSearchFocus.queryType,
        },
      ];
    }
    if (this.showSearchValuePicker && this.searchValueOptions.length) {
      return this.searchValueOptions.map((option) => ({
        value: option.value,
        label: option.label,
        type: option.type,
      }));
    }
    const options: Array<{ value: string; label: string; type: string }> = [];
    this.searchableValueRows.forEach((row) => {
      row.values.forEach((value) => {
        options.push({
          value,
          label: row.key === 'image' ? 'Photo' : `${value} (${row.label})`,
          type: row.key,
        });
      });
    });
    return options;
  }

  isSearchValueSelected(value: string): boolean {
    return this.selectedSearchValues.includes(value);
  }

  onSearchValueSelectionChange(value: string, event: Event): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement | null;
    this.searchValueSelect.emit({ value, selected: Boolean(input?.checked) });
  }

  trackSearchValueOption(_index: number, option: { value: string }): string {
    return option.value;
  }

  /** Prefer gallery/proxy src when available; keep option.value for the actual search. */
  getSearchValueImageSrc(option: { value: string; type?: string }): string {
    const url = String(option?.value || '').trim();
    if (!url) return '';
    const fromGallery = this.findGalleryImageForUrl(url);
    if (fromGallery?.src) return fromGallery.src;
    const displayed = this.displayImageUrl(url);
    if (displayed) return displayed;
    // Never put the raw auth-gated proxy URL in the thumb (401 → silhouette).
    if (isAuthGatedOsintImageUrl(url)) return '';
    return this.isDisplayablePictureSrc(url) ? url : '';
  }

  onSearchValueImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img) return;
    if (img.dataset['searchThumbFallback'] === '1') {
      img.style.display = 'none';
      return;
    }
    img.dataset['searchThumbFallback'] = '1';
    img.classList.add('is-fallback');
    img.src = DEFAULT_PROFILE_AVATAR_SRC;
  }

  get selectedRunCount(): number {
    const actions = this.selectedActions.filter((action) => !action.disabled && !action.running);
    if (!actions.length) return 0;
    if (!this.valueOptions.length) return actions.length;
    if (!this.selectedSearchValues.length) return 0;

    let count = 0;
    actions.forEach((action) => {
      const matchingValues = this.valueOptions.filter(
        (option) =>
          this.selectedSearchValues.includes(option.value) && option.type === action.queryType &&
          !action.completedValues?.includes(option.value),
      );
      count += matchingValues.length;
    });
    return count;
  }

  get selectedActions(): NodeActionItem[] {
    const actions = this.popoverNodeActions;
    if (!actions.length || !this.selectedActionKeys.length) return [];
    return actions.filter((action) => this.selectedActionKeys.includes(action.key));
  }

  get canRunSelectedAction(): boolean {
    const actions = this.selectedActions.filter((action) => !action.disabled && !action.running);
    if (!actions.length) return false;
    if (this.valueOptions.length > 0 && !this.selectedSearchValues.length) return false;
    return this.selectedRunCount > 0;
  }

  get runSelectedActionsLabel(): string {
    const selected = this.selectedActions;
    if (selected.some((action) => action.running)) return 'Running...';
    const runCount = this.selectedRunCount;
    if (runCount <= 1) return 'Run selected action';
    return `Run ${runCount} selected actions`;
  }

  onStarterQueryTypeSelect(type: 'phone' | 'email' | 'username'): void {
    this.starterQueryTypeSelect.emit(type);
  }

  onStarterQueryValueChange(value: string): void {
    this.starterQueryValueChange.emit(value);
  }

  onRunStarterSearch(): void {
    this.runStarterSearch.emit();
  }

  onRunAi(): void {
    this.runAi.emit();
  }

  shouldShowSearchPopover(): boolean {
    if (!this.searchPopover) return false;
    return (
      this.isInvestigationRoot(this.searchPopover.nodeId) ||
      this.showSearchPanel ||
      this.popoverNodeActions.length > 0 ||
      this.showSearchValuePicker ||
      this.showStarterSearch ||
      this.searchableValueRows.length > 0 ||
      this.valueOptions.length > 0 ||
      Boolean(this.detailFieldSearchFocus) ||
      (this.isAggregatePopover && this.aggregateViewItems.length > 0)
    );
  }

  requestAddRootNode(): void {
    this.closeSearchPopover();
    this.addNodeRequested.emit();
  }

  isInvestigationRoot(nodeId?: string): boolean {
    return !!nodeId && nodeId === (this.graphPayload?.rootNode?.id || this.graphPayload?.rootNodeId || this.graphData.nodes[0]?.id);
  }

  popoverActionLabel(action: NodeActionItem): string {
    return action.label.replace(/^Run\s+/i, '').replace(/\s+search$/i, '');
  }

  refreshSearchPopoverActions(nodeId: string): void {
    if (this.selectedNode?.id !== nodeId && this.highlightedNodeId !== nodeId) return;
    this.cdr.markForCheck();
  }

  openSearchPopover(nodeId: string, anchor?: { x: number; y: number }): void {
    const node = this.graphData.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const popoverWidth = Math.min(360, Math.max(280, window.innerWidth - 24));
    const popoverHeightEstimate = 320;
    const panelLeft =
      this.isPanelOpen && this.selectedNode
        ? this.detailsPanel?.nativeElement?.getBoundingClientRect().left ??
        window.innerWidth - this.effectiveDetailsPanelWidth
        : window.innerWidth;
    const maxX = Math.max(12, panelLeft - popoverWidth - 12);
    let x: number;
    let y: number;
    if (anchor) {
      x = anchor.x + 8;
      y = anchor.y + 8;
    } else {
      const screen = this.getNodeScreenPosition(nodeId);
      x = (screen?.x ?? window.innerWidth / 2) + 14;
      y = (screen?.y ?? window.innerHeight / 2) + 20;
    }
    this.searchPopover = {
      nodeId,
      node,
      x: Math.min(Math.max(12, x), maxX),
      y: Math.min(Math.max(12, y), window.innerHeight - popoverHeightEstimate),
      pinned: true,
    };
  }

  closeSearchPopover(): void {
    const hadDetailFocus = Boolean(this.detailFieldSearchFocus);
    this.searchPopover = null;
    this.detailFieldSearchFocus = null;
    this.stopSearchPopoverDrag();
    if (hadDetailFocus) {
      this.clearDetailFieldSearch.emit();
    }
    if (!this.isPanelOpen) {
      this.highlightedNodeId = null;
    }
    this.graph?.refresh?.();
  }

  onSearchPopoverHeaderPointerDown(event: PointerEvent): void {
    if (!this.searchPopover) return;
    event.preventDefault();
    event.stopPropagation();
    this.stopSearchPopoverDrag();
    this.searchPopover = { ...this.searchPopover, pinned: true };
    this.searchPopoverDragState = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: this.searchPopover.x,
      originY: this.searchPopover.y,
    };
    this.searchPopoverDragMoveHandler = (moveEvent: PointerEvent) => {
      if (
        !this.searchPopoverDragState ||
        moveEvent.pointerId !== this.searchPopoverDragState.pointerId ||
        !this.searchPopover
      ) {
        return;
      }
      const dx = moveEvent.clientX - this.searchPopoverDragState.startClientX;
      const dy = moveEvent.clientY - this.searchPopoverDragState.startClientY;
      this.searchPopover = {
        ...this.searchPopover,
        x: Math.min(Math.max(8, this.searchPopoverDragState.originX + dx), window.innerWidth - 380),
        y: Math.min(Math.max(8, this.searchPopoverDragState.originY + dy), window.innerHeight - 120),
      };
      this.cdr.markForCheck();
    };
    this.searchPopoverDragEndHandler = () => this.stopSearchPopoverDrag();
    document.addEventListener('pointermove', this.searchPopoverDragMoveHandler);
    document.addEventListener('pointerup', this.searchPopoverDragEndHandler);
    document.addEventListener('pointercancel', this.searchPopoverDragEndHandler);
  }

  private stopSearchPopoverDrag(): void {
    if (this.searchPopoverDragMoveHandler) {
      document.removeEventListener('pointermove', this.searchPopoverDragMoveHandler);
      this.searchPopoverDragMoveHandler = null;
    }
    if (this.searchPopoverDragEndHandler) {
      document.removeEventListener('pointerup', this.searchPopoverDragEndHandler);
      document.removeEventListener('pointercancel', this.searchPopoverDragEndHandler);
      this.searchPopoverDragEndHandler = null;
    }
    this.searchPopoverDragState = null;
  }

  private getNodeScreenPosition(nodeId: string): { x: number; y: number } | null {
    if (!this.graph || !this.graphContainerEl) return null;
    const graphNode = (this.graph.graphData?.()?.nodes ?? []).find((n: any) => n?.id === nodeId);
    if (!graphNode || !Number.isFinite(graphNode.x) || !Number.isFinite(graphNode.y)) {
      return null;
    }
    const coords = this.graph.graph2ScreenCoords?.(graphNode.x, graphNode.y);
    if (!coords) return null;
    const rect = this.graphContainerEl.getBoundingClientRect();
    return { x: rect.left + coords.x, y: rect.top + coords.y };
  }

  setTag(tag: NodeTag): void {
    if (!this.selectedNode) return;
    const current = this.annotations.getAnnotation(this.selectedNode.id).tag;
    this.annotations.setTag(this.selectedNode.id, current === tag ? undefined : tag);
    this.graph?.refresh?.();
  }

  saveNote(): void {
    if (!this.selectedNode) return;
    this.annotations.setNote(this.selectedNode.id, this.noteDraft);
    this.noteSaved = true;
    setTimeout(() => (this.noteSaved = false), 1500);
  }

  highlightCorrelation(nodeIds: string[]): void {
    this.correlationHighlight = new Set(nodeIds);
    this.pushGraphData();
    this.graph?.refresh?.();
  }

  closeCorrelation(): void {
    this.correlationHighlight = new Set();
    this.correlationOpen = false;
    this.graph?.refresh?.();
  }

  onTimelineStepChange(index: number): void {
    this.timelineStepIndex = index;
    this.timelinePlaying = false;
    this.applyTimelineFilter();
  }

  toggleTimelinePlay(): void {
    if (this.timelineStepIndex >= this.timelineDates.length - 1) {
      this.timelineStepIndex = 0;
    }
    this.timelinePlaying = !this.timelinePlaying;
    if (this.timelinePlaying) {
      this.timelineInterval = setInterval(() => {
        if (this.timelineStepIndex >= this.timelineDates.length - 1) {
          this.timelinePlaying = false;
          if (this.timelineInterval) clearInterval(this.timelineInterval);
          return;
        }
        this.timelineStepIndex += 1;
        this.applyTimelineFilter();
      }, 1200);
    } else if (this.timelineInterval) {
      clearInterval(this.timelineInterval);
    }
  }

  stopTimeline(): void {
    this.timelinePlaying = false;
    if (this.timelineInterval) clearInterval(this.timelineInterval);
  }

  closeTimeline(): void {
    this.stopTimeline();
    this.timelineVisibleIds = null;
    this.timelineOpen = false;
    this.recomputeFilteredData();
    this.pushGraphData();
  }

  showAllTimeline(): void {
    this.timelineStepIndex = this.timelineDates.length - 1;
    this.timelineVisibleIds = null;
    this.timelinePlaying = false;
    this.recomputeFilteredData();
    this.pushGraphData();
  }

  private mapInitToken = 0;

  openMap(): void {
    this.mapOpen = true;
    this.mapLoading = true;
    this.mapMarkerCount = 0;
    setTimeout(() => void this.initMap(), 50);
  }

  closeMap(): void {
    this.mapOpen = false;
    this.mapLoading = false;
    this.mapMarkerCount = 0;
    this.mapInitToken += 1;
    this.destroyMap();
  }

  exportCsv(kind: 'entities' | 'relationships'): void {
    const ts = new Date().toISOString().slice(0, 10);
    const content = kind === 'entities' ? entitiesToCSV(this.graphData) : relationshipsToCSV(this.graphData);
    this.downloadFile(`osint-${kind}-${ts}.csv`, content, 'text/csv');
    this.exportOpen = false;
  }

  exportJson(): void {
    const ts = new Date().toISOString().slice(0, 10);
    this.downloadFile(`osint-dataset-${ts}.json`, exportToJSON(this.graphData), 'application/json');
    this.exportOpen = false;
  }

  async exportPdfDossier(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.graphData.nodes.length) {
      this.toastr.error('No graph data available to export.');
      return;
    }

    this.exportingPdf = true;
    this.cdr.markForCheck();

    try {
      const screenshot = await this.captureGraphScreenshot();
      const graphScreenshotDataUrl = screenshot
        ? await this.compressScreenshot(screenshot)
        : null;

      const imageSources = collectDossierImageSources(
        this.investigation || { graph_payload: this.graphPayload },
        this.graphData,
      );
      const evidenceImages = await this.linkAnalysisExportService.embedImagesForPdf(imageSources);

      await this.linkAnalysisExportService.exportDossierPdf({
        investigation: this.investigation || { graph_payload: this.graphPayload },
        graphData: this.graphData,
        correlations: this.correlations,
        graphScreenshotDataUrl,
        evidenceImages,
        exportedBy: this.resolveExportedByName(),
      });
      this.toastr.success('PDF dossier report downloaded.');
    } catch (error) {
      console.error('PDF dossier export failed', error);
      this.toastr.error('Unable to export PDF dossier report. Please try again.');
    } finally {
      this.exportingPdf = false;
      this.cdr.markForCheck();
    }
  }

  private compressScreenshot(dataUrl: string): Promise<string> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const maxWidth = 1100;
        const scale = Math.min(1, maxWidth / Math.max(image.width, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(dataUrl);
          return;
        }
        context.fillStyle = this.getGraphBackgroundColor();
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      image.onerror = () => resolve(dataUrl);
      image.src = dataUrl;
    });
  }

  private async captureGraphScreenshot(): Promise<string | null> {
    if (!this.graph) return null;

    this.fitGraphToView(0);
    this.graph.refresh?.();
    await new Promise((resolve) => setTimeout(resolve, 350));

    const canvas = this.graphContainerEl?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return null;

    try {
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.warn('Unable to capture graph screenshot', error);
      return null;
    }
  }

  private resolveExportedByName(): string {
    if (typeof localStorage === 'undefined') return 'RYNO OSINT Link Analysis';
    try {
      const raw = localStorage.getItem('userPortal');
      if (!raw) return 'RYNO OSINT Link Analysis';
      const userPortal = JSON.parse(raw);
      return (
        userPortal?.loginDetails?.user_name ||
        userPortal?.loginDetails?.user_email ||
        'RYNO OSINT Link Analysis'
      );
    } catch {
      return 'RYNO OSINT Link Analysis';
    }
  }

  onCmdQueryChange(): void {
    this.refreshCmdItems();
    this.cmdFocusedIndex = 0;
  }

  runCmdItem(index: number): void {
    const item = this.cmdItems[index];
    if (!item) return;
    item.run();
    this.closeCommandPalette();
  }

  onCmdKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.cmdFocusedIndex = Math.min(this.cmdFocusedIndex + 1, this.cmdItems.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.cmdFocusedIndex = Math.max(this.cmdFocusedIndex - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.runCmdItem(this.cmdFocusedIndex);
    } else if (event.key === 'Escape') {
      this.closeCommandPalette();
    }
  }

  getNodeById(id: string): ViewerNode | undefined {
    return this.graphData.nodes.find((n) => n.id === id);
  }

  treeContext(node: TreeNode, depth: number): { $implicit: TreeNode; depth: number } {
    return { $implicit: node, depth };
  }

  entityIcon(type: EntityType): string {
    return this.entityIcons[type] ?? '?';
  }

  entityLabel(type: EntityType): string {
    return this.entityLabels[type] ?? type;
  }

  /** Detect phone/email/url in a display value for detail panel coloring. */
  getDetailValueEntityType(value: string | null | undefined): 'phone' | 'email' | 'url' | null {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;

    if (/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(text)) return 'email';

    if (/^(https?:\/\/|www\.)/i.test(text) || /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/|\?|#|$)/i.test(text)) {
      if (!/^\+?\d[\d\s().-]{6,}$/.test(text)) return 'url';
    }

    // Phone: +digits / digit-heavy string; allow trailing suffix like " (Leak)" / " (OSINT)"
    const phoneCandidate = text.replace(/\s*\([^)]*\)\s*$/, '').trim();
    const digits = phoneCandidate.replace(/\D/g, '');
    const looksLikePhone =
      /^\+?[\d\s().-]{7,}$/.test(phoneCandidate) && digits.length >= 7 && digits.length <= 15;
    return looksLikePhone ? 'phone' : null;
  }

  /** Color phone/email/url text values in the detail panel. */
  getDetailValueColor(value: string | null | undefined): string | null {
    const type = this.getDetailValueEntityType(value);
    if (!type) return null;
    if (type === 'url') return '#58a6ff';
    return this.entityColors[type];
  }

  isTagActive(tag: NodeTag): boolean {
    if (!this.selectedNode) return false;
    return this.annotations.getAnnotation(this.selectedNode.id).tag === tag;
  }

  nodeLabelById(id: string): string {
    return this.getNodeById(id)?.label ?? id;
  }

  isIdentityHub(node: ViewerNode | null | undefined): boolean {
    return isIdentityHubNode(node);
  }

  isTreeNodeSelected(nodeId: string): boolean {
    return this.selectedNode?.id === nodeId;
  }

  isHierarchyNodeCollapsed(nodeId: string): boolean {
    return this.isCollapsibleHierarchyNode(nodeId) && !this.expandedBranchIds.has(nodeId);
  }

  handleGraphNodeClick(node: any, _event?: MouseEvent): void {
    if (node?.__searchJunctionKey) return;
    this.activateNode(node?.id);
  }

  handleGraphNodeRightClick(node: any, event: MouseEvent): void {
    event.preventDefault();
    if (node?.__searchJunctionKey) return;
    if (!this.graphData.nodes.some((item) => item.id === node?.id)) return;
    this.isPanelOpen = false;
    this.isPanelCollapsed = false;
    this.activateNode(node.id, false);
    this.openSearchPopover(node.id, { x: event.clientX, y: event.clientY });
  }

  getPaintedNodeRadius(node: any): number {
    if (this.viewMode === 'tree') {
      return 22;
    }
    const id = node?.id ?? '';
    const type = node?.type as EntityType;
    const count = this.degreeMap[id] ?? 1;
    const baseSize = type === 'person' ? 22 : type === 'phone' || type === 'email' ? 21 : 19;
    return baseSize + Math.min(count * 0.35, 4);
  }

  getBranchBadgeMetrics(
    node: any,
    size = this.getPaintedNodeRadius(node),
  ): { bx: number; by: number; badgeR: number } {
    const x = node?.x ?? 0;
    const y = node?.y ?? 0;
    const badgeR = Math.max(size * 0.42, 4.5);
    return {
      bx: x + size * 0.78,
      by: y + size * 0.78,
      badgeR,
    };
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  private getExpandedGraphPayload(): any {
    const nodes = this.graphPayload?.nodes || [];
    const edges = this.graphPayload?.edges || [];
    if (!nodes.length) {
      return this.graphPayload;
    }

    const rootId =
      this.graphPayload?.rootNode?.id ||
      this.graphPayload?.rootNodeId ||
      nodes.find((node: any) => String(node?.type || '').toLowerCase() === 'query_root')?.id ||
      nodes.find((node: any) => node.type === 'starter')?.id ||
      nodes[0]?.id ||
      '';

    // Keep collected photos in their owning source's panel, not on the canvas.
    const collapsed = foldCollectedPhotoNodes(nodes, edges);
    const withCollections = ensureNamesAndPhotosCollectionHubs(
      collapsed.nodes,
      collapsed.edges,
      rootId,
    );
    const expanded = expandGraphWithSourceTypeHubs(withCollections.nodes, withCollections.edges, {
      rootId,
      getNodeLayer: (node) => {
        if (node?.id === rootId || node?.type === 'starter') return 0;
        const metaLayer = Number(node?.metadata?.layer);
        return Number.isFinite(metaLayer) ? metaLayer : 1;
      },
    });

    return {
      ...(this.graphPayload || {}),
      nodes: expanded.nodes,
      edges: expanded.edges,
    };
  }

  private refreshDerivedData(): void {
    const expandedPayload = this.getExpandedGraphPayload();
    const nextGraph = mapPayloadToViewerGraph(expandedPayload, {
      rootValue: this.investigation?.root_value,
    });
    const nextRootId = resolveDefaultRootId(
      nextGraph,
      expandedPayload?.rootNode?.id || expandedPayload?.rootNodeId || this.graphPayload?.rootNode?.id || this.graphPayload?.rootNodeId,
    );
    const investigationId = String(this.investigation?.link_analysis_id || '');
    const sameGraph = this.graphData.nodes.length > 0 && nextRootId === this.rootId && investigationId === this.layoutInvestigationId;
    const layoutSignature = (data: ViewerGraphData, rootId: string): string => {
      const edges = getHierarchyEdges(data, rootId);
      return JSON.stringify([
        data.nodes.map((node) => node.id),
        edges.map((edge) => [graphEndpointId(edge.source), graphEndpointId(edge.target)]),
        groupSearchTrunks(edges, data.nodes, rootId).map((group) => [group.key, group.links.map((link) => graphEndpointId(link.target))]),
      ]);
    };
    const structureChanged = !sameGraph || layoutSignature(this.graphData, this.rootId) !== layoutSignature(nextGraph, nextRootId);
    if (!sameGraph) {
      this.graphDataSignature = '';
      this.initialViewportApplied = false;
      this.networkNodePositions.clear();
      this.treeNodePositions.clear();
      this.treeCompactPositions.clear();
      this.junctionPositions.clear();
      this.searchJunctionNodes.clear();
      this.treeZoomSpreadApplied = 1;
      this.branchLayers = [];
      this.expandedBranchIds = new Set();
      this.branchLayersSignature = '';
      this.pendingTreeLayoutAnchor = null;
    } else if (structureChanged && this.viewMode === 'tree') {
      const anchorId = this.selectedNode?.id || this.rootId;
      const anchor = this.graph?.graphData?.()?.nodes?.find((node: any) => node.id === anchorId);
      if (anchor && Number.isFinite(anchor.x) && Number.isFinite(anchor.y)) {
        this.pendingTreeLayoutAnchor = { id: anchorId, x: anchor.x / this.treeZoomSpreadApplied, y: anchor.y };
      }
    }
    this.treeLayoutDirty = this.treeLayoutDirty || structureChanged;
    this.graphData = nextGraph;
    this.rootId = nextRootId;
    this.layoutInvestigationId = investigationId;
    this.degreeMap = getDegreeMap(this.graphData);
    this.adjacency = buildAdjacency(this.graphData);
    this.hopDistances = getHopDistances(this.graphData, this.rootId);
    this.correlations = detectCorrelations(this.graphData);
    this.timelineDates = getTimelineDates(this.graphData);
    this.timelineStepIndex = Math.max(0, this.timelineDates.length - 1);
    this.refreshHierarchy();
    this.refreshInsights();
    this.refreshOsintGalleryImages();
    this.preloadImages();
    this.recomputeFilteredData();
  }

  private refreshHierarchy(): void {
    this.hierarchyEdges = getHierarchyEdges(this.graphData, this.rootId);
    this.tree = buildHierarchy(this.graphData, this.rootId);
    this.refreshBranchLayers();
  }

  private refreshBranchLayers(): void {
    if (!this.tree) {
      this.branchLayers = [];
      this.expandedBranchIds = new Set();
      this.branchLayersSignature = '';
      return;
    }

    const collapsedBefore = new Set(
      this.branchLayers.filter((b) => !this.expandedBranchIds.has(b.id)).map((b) => b.id),
    );
    const branches: Array<{ id: string; label: string; number: number; color: string }> = [];
    const walk = (treeNode: TreeNode) => {
      if (this.isCollapsibleBranchNode(treeNode.node.id)) {
        branches.push({
          id: treeNode.node.id,
          label: treeNode.node.label,
          number: branches.length + 1,
          color: this.getBranchLayerColor(treeNode.node, branches.length),
        });
      }
      treeNode.children.forEach(walk);
    };
    walk(this.tree);

    const signature = branches.map((b) => b.id).join('|');
    if (signature === this.branchLayersSignature) return;

    this.branchLayersSignature = signature;
    this.branchLayers = branches;
    const nextExpanded = new Set<string>();
    branches.forEach((branch) => {
      if (!collapsedBefore.has(branch.id)) {
        nextExpanded.add(branch.id);
      }
    });
    this.expandedBranchIds = nextExpanded;
  }

  private getBranchLayerColor(node: ViewerNode, index: number): string {
    const raw = node.raw || {};
    const sourceType = resolveNodeSourceType(raw);
    if (sourceType && LinkAnalysisGraphDashboardComponent.BRANCH_LAYER_COLORS[sourceType]) {
      return LinkAnalysisGraphDashboardComponent.BRANCH_LAYER_COLORS[sourceType];
    }
    const type = String(raw.type || node.type || '').toLowerCase();
    if (type === 'ai_analysis') return LinkAnalysisGraphDashboardComponent.BRANCH_LAYER_COLORS['ai_analysis'];
    if (type === 'leak_source') return LinkAnalysisGraphDashboardComponent.BRANCH_LAYER_COLORS['leak'];
    const palette = ['#14b8a6', '#8b5cf6', '#f59e0b', '#3b82f6'];
    return palette[index % palette.length];
  }

  private refreshInsights(): void {
    const typeDist: Record<string, number> = {};
    this.graphData.nodes.forEach((n) => {
      typeDist[n.type] = (typeDist[n.type] ?? 0) + 1;
    });
    this.insightsTypeDist = typeDist;
    this.insightsTopNodes = [...this.graphData.nodes]
      .map((node) => ({ node, degree: this.degreeMap[node.id] ?? 0 }))
      .sort((a, b) => b.degree - a.degree)
      .slice(0, 6);
    const riskCounts: Record<string, number> = { high: 0, medium: 0, low: 0 };
    this.graphData.nodes.forEach((n) => {
      const meta = deriveMeta(n, this.degreeMap[n.id] ?? 0);
      riskCounts[meta.risk] = (riskCounts[meta.risk] ?? 0) + 1;
    });
    this.insightsRiskCounts = riskCounts;
    this.insightsMaxType = Math.max(1, ...Object.values(typeDist));
    this.insightsMaxDegree = Math.max(1, ...this.insightsTopNodes.map((t) => t.degree));
  }

  private recomputeFilteredData(): void {
    const { nodes, links } = this.computeFilteredData();
    this.filteredNodes = nodes;
    this.filteredLinks = links;
    this.treeBranchNodeIds = new Set<string>();
    if (this.viewMode === 'tree') {
      links.forEach((link) => {
        const sourceId = link?.source?.id ?? link?.source;
        if (sourceId) this.treeBranchNodeIds.add(sourceId);
      });
    }
  }

  private shouldShowNodeLabel(
    nodeId: string,
    inFocus: boolean,
    isActive: boolean,
    globalScale: number,
    isImportant: boolean,
  ): boolean {
    if (!inFocus) return this.viewMode === 'tree' || globalScale >= this.nodeLabelZoomThreshold;
    if (this.viewMode === 'tree') return true;
    if (isActive || isImportant) return true;
    return globalScale >= this.nodeLabelZoomThreshold;
  }

  private formatPaintedNodeLabel(label: string, maxChars = this.nodeLabelMaxChars): string {
    const text = String(label || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    if (text.length <= maxChars) return text;
    return `${text.slice(0, Math.max(4, maxChars - 1)).trim()}…`;
  }

  /** Source hub names sit above the node; all other values stay below. */
  private paintNodeNameLabel(
    ctx: CanvasRenderingContext2D,
    node: ViewerNode,
    x: number,
    y: number,
    size: number,
    globalScale: number,
    isActive: boolean,
  ): void {
    const service = this.isPlatformBrandNode(node);
    const category = service ? this.getNodePlatformKey(node) || node.label : getGraphCategoryLabel(node);
    const primaryValue = service ? this.getServiceNodePresentation(node).value : node.label;
    const paintedLabel = this.formatPaintedNodeLabel(primaryValue || '', this.nodeLabelMaxChars);
    if (this.viewMode === 'tree') {
      // Preserve an overview minimum, then enlarge text gradually with zoom.
      const scale = Math.max(globalScale, 0.01);
      const labelGrowth = Math.min(3, Math.pow(Math.max(1, scale / 0.25), 0.4));
      const fontSize = 10.5 * labelGrowth / scale;
      const labelY = y + size + 5 / scale;
      ctx.save();
      ctx.font = `600 ${fontSize}px 'Segoe UI', sans-serif`;
      const prominent = isActive || node.id === this.rootId || this.treeBranchNodeIds.has(node.id);
      const maxWidth = (prominent ? 180 * labelGrowth : Math.max(24, Math.min(180 * labelGrowth, 170 * scale))) / scale;
      let label = paintedLabel;
      while (label.length > 3 && ctx.measureText(label).width > maxWidth) {
        label = label.slice(0, -2).trimEnd() + '\u2026';
      }
      const width = ctx.measureText(label).width;
      ctx.fillStyle = this.getLinkLabelBackgroundColor();
      ctx.fillRect(x - width / 2 - 3 / scale, labelY - 2 / scale, width + 6 / scale, fontSize + 4 / scale);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = this.getNodeLabelTextColor(isActive);
      ctx.fillText(label, x, labelY);
      if ((service || globalScale >= 0.5) && category) {
        ctx.font = `${service ? 600 : 500} ${(service ? 10.5 : 8.5) * labelGrowth / scale}px 'Segoe UI', sans-serif`;
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = service ? this.getNodeLabelTextColor(isActive) : this.getNodeCategoryTextColor();
        let categoryLabel = category;
        while (categoryLabel.length > 3 && ctx.measureText(categoryLabel).width > maxWidth) {
          categoryLabel = categoryLabel.slice(0, -2).trimEnd() + '\u2026';
        }
        const categoryWidth = ctx.measureText(categoryLabel).width;
        const categoryY = y - size - 5.5 / scale;
        if (service) {
          ctx.fillStyle = this.getLinkLabelBackgroundColor();
          ctx.fillRect(x-categoryWidth/2-3/scale, categoryY-fontSize-2/scale,categoryWidth+6/scale,fontSize+4/scale);
          ctx.fillStyle = this.getNodeLabelTextColor(isActive);
        }
        ctx.fillText(categoryLabel, x, categoryY);
      }
      ctx.restore();
      return;
    }
    paintGraphNodeLabels(ctx, {
      category,
      value: paintedLabel,
      x,
      y,
      size,
      globalScale,
      categoryColor: this.getNodeCategoryTextColor(),
      valueColor: this.getNodeLabelTextColor(isActive),
    });
  }

  private updateHoverTooltip(node: any | null): void {
    if (!node?.id) {
      this.hoverTooltipLabel = null;
      this.cdr.markForCheck();
      return;
    }
    const fullLabel = String(node.label || node.id || '').trim();
    if (!fullLabel) {
      this.hoverTooltipLabel = null;
      this.cdr.markForCheck();
      return;
    }
    this.hoverTooltipLabel = fullLabel;
    const screen = this.graph?.graph2ScreenCoords?.(node.x ?? 0, node.y ?? 0);
    const size = this.getPaintedNodeRadius(node);
    const scale = this.graph?.zoom?.() ?? 1;
    if (screen && Number.isFinite(screen.x) && Number.isFinite(screen.y)) {
      this.hoverTooltipX = screen.x;
      this.hoverTooltipY = screen.y + size * scale + 10;
    }
    this.cdr.markForCheck();
  }

  private computeFilteredData(): { nodes: ViewerNode[]; links: any[] } {
    let baseNodes = this.graphData.nodes.filter(
      (n) =>
        this.visibleLayers.has(n.type) &&
        !isIdentityHubChildNode(n) &&
        shouldDisplayOsintSourceOnGraph(n.raw || n),
    );

    if (this.timelineVisibleIds) {
      baseNodes = baseNodes.filter((n) => this.timelineVisibleIds!.has(n.id));
    }

    if (this.hopFilter != null) {
      baseNodes = baseNodes.filter((n) => {
        const d = this.hopDistances[n.id];
        return d != null && d <= this.hopFilter!;
      });
    }

    if (this.isolatedId) {
      const keep = new Set<string>([
        this.isolatedId,
        ...Array.from(this.adjacency.get(this.isolatedId) ?? []),
      ]);
      baseNodes = baseNodes.filter((n) => keep.has(n.id));
    }

    if (
      this.viewMode === 'tree' &&
      this.branchLayers.length &&
      this.expandedBranchIds.size < this.branchLayers.length
    ) {
      const hidden = this.getHierarchyHiddenNodeIds();
      baseNodes = baseNodes.filter((n) => !hidden.has(n.id));
    }

    const visibleNodeIds = new Set(baseNodes.map((n) => n.id));
    const edgeSource = this.viewMode === 'tree' ? this.hierarchyEdges : this.graphData.edges;
    const links = edgeSource
      .filter((e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target))
      .map((e) => ({ ...e }));

    return {
      nodes: baseNodes.map((n) => ({ ...n })),
      links,
    };
  }

  private hasActiveGraphFilters(): boolean {
    return (
      this.hopFilter != null ||
      !!this.isolatedId ||
      !!this.timelineVisibleIds ||
      this.visibleLayers.size < this.layerOrder.length
    );
  }

  private ensureVisibleGraphData(): void {
    if (this.filteredNodes.length || !this.graphData.nodes.length) {
      return;
    }
    if (this.visibleLayers.size === 0) {
      return;
    }
    if (!this.hasActiveGraphFilters()) {
      return;
    }
    this.hopFilter = null;
    this.isolatedId = null;
    this.timelineVisibleIds = null;
    this.recomputeFilteredData();
  }

  private pushGraphData(): void {
    this.invalidateGraphIfDetached();
    if (!this.graph) {
      this.queueGraphSync();
      return;
    }

    this.ensureVisibleGraphData();
    this.recomputeFilteredData();

    const signature = `${this.viewMode}|${this.filteredNodes.length}|${this.filteredLinks.length}|${this.filteredNodes.map((n) => n.id).join(',')}`;
    const structureChanged = signature !== this.graphDataSignature;
    this.graphDataSignature = signature;

    const staticTreePositions =
      this.viewMode === 'tree' && (structureChanged || this.treeLayoutDirty)
        ? this.computeTreeLayoutPositions()
        : null;

    const nodes = this.filteredNodes.map((n) => {
      const node: any = { ...n };
      const staticPos = staticTreePositions?.[n.id];
      if (staticPos) {
        node.x = staticPos.x;
        node.y = staticPos.y;
        node.fx = staticPos.x;
        node.fy = staticPos.y;
        this.treeCompactPositions.set(n.id, { x: staticPos.x, y: staticPos.y });
        this.treeNodePositions.set(n.id, {
          x: staticPos.x,
          y: staticPos.y,
          fx: staticPos.x,
          fy: staticPos.y,
        });
        return node;
      }

      const savedPos =
        this.viewMode === 'tree'
          ? this.treeNodePositions.get(n.id)
          : this.networkNodePositions.get(n.id);
      if (savedPos) {
        node.x = savedPos.x;
        node.y = savedPos.y;
        if (this.viewMode === 'tree') {
          const treePos = savedPos as { x: number; y: number; fx?: number; fy?: number };
          node.fx = treePos.fx ?? treePos.x;
          node.fy = treePos.fy ?? treePos.y;
        }
        return node;
      }

      return node;
    });
    const links = this.filteredLinks.map((e) => ({ ...e }));
    const previousJunctions = this.searchJunctionNodes;
    this.searchJunctionNodes = new Map();
    this.searchTrunkByLink.clear();
    if (this.viewMode === 'tree') {
      const byId = new Map(nodes.map(node => [node.id, node]));
      const groups = groupSearchTrunks(links, nodes, this.rootId);
      const targetsByParent = new Map<string, any[]>();
      groups.forEach(group => {
        const targets = targetsByParent.get(group.sourceId) ?? [];
        group.links.forEach(link => {
          const target = byId.get(graphEndpointId(link.target));
          if (target) targets.push(target);
        });
        targetsByParent.set(group.sourceId, targets);
      });
      groups.forEach((group) => {
        group.railOffset = this.treeTrunkRailOffsets.get(group.key);
        group.links.forEach((link) => this.searchTrunkByLink.set(link, group));
        const source = byId.get(group.sourceId);
        const targets = group.links.map(link => byId.get(graphEndpointId(link.target))).filter(Boolean);
        if (!source || !targets.length) return;
        const spread = staticTreePositions ? 1 : this.treeZoomSpreadApplied;
        const placed = this.junctionPositions.get(group.key);
        const start = { x: source.x, y: source.y + this.getPaintedNodeRadius(source) + 2 };
        const commonStemLength = searchFanGeometry(start, targetsByParent.get(group.sourceId) ?? targets).stem[1].y - start.y;
        const point = placed ? { x: placed.x * spread, y: placed.y } : searchFanGeometry(
          start, targets, undefined, commonStemLength,
        ).junction;
        const junction = previousJunctions.get(group.key) || { id: `__ui_search_junction__${group.key}` };
        Object.assign(junction, { label: group.label, __searchJunctionKey: group.key, __searchJunctionGroup: group,
          x: point.x, y: point.y, fx: point.x, fy: point.y });
        this.searchJunctionNodes.set(group.key, junction);
        this.treeCompactPositions.set(junction.id, { x: point.x / spread, y: point.y });
        this.treeNodePositions.set(junction.id, { x: point.x, y: point.y, fx: point.x, fy: point.y });
        nodes.push(junction);
      });
    }

    if (staticTreePositions) {
      this.subtreeDragSession = null;
      this.treeLayoutDirty = false;
      this.treeZoomSpreadApplied = 1;
    }

    this.graph.graphData({ nodes, links });
    if (structureChanged && this.viewMode !== 'tree') {
      this.graph.d3ReheatSimulation?.();
    } else if (this.viewMode === 'tree') {
      this.freezeTreeSimulation();
      this.applyTreeZoomSpacing(true);
    }
    this.resizeGraph();
    this.graph.refresh?.();

    if (nodes.length) {
      this.graphEngineError = '';
    }
  }

  private scheduleLayoutRefresh(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      this.resizeGraph();
      this.graph?.refresh?.();
      return;
    }
    if (this.layoutRefreshRaf != null) {
      cancelAnimationFrame(this.layoutRefreshRaf);
    }
    this.layoutRefreshRaf = requestAnimationFrame(() => {
      this.layoutRefreshRaf = null;
      this.invalidateGraphIfDetached();
      if (!this.graph) {
        this.queueGraphSync();
        return;
      }
      this.resizeGraph();
      this.graph.refresh?.();
    });
  }

  private isGraphDomMounted(): boolean {
    return !!this.graphContainerEl?.querySelector('canvas');
  }

  private invalidateGraphIfDetached(): void {
    if (!this.graph) return;
    if (this.isGraphDomMounted()) return;
    this.graph._destructor?.();
    this.graph = undefined;
    this.graphInitPromise = null;
  }

  private queueGraphSync(): void {
    if (this.graphSyncTimer) clearTimeout(this.graphSyncTimer);
    this.graphSyncTimer = setTimeout(() => {
      void this.syncGraph();
      setTimeout(() => void this.syncGraph(), 120);
      setTimeout(() => void this.syncGraph(), 400);
      setTimeout(() => void this.syncGraph(), 1000);
    }, 0);
  }

  private observeGraphContainer(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.graphResizeObserver?.disconnect();
    const canvas = this.graphContainerEl;
    if (!canvas) return;
    this.graphResizeObserver = new ResizeObserver(() => {
      this.syncDetailsPanelWidth();
      this.scheduleLayoutRefresh();
    });
    this.graphResizeObserver.observe(canvas);
    const stage = canvas.parentElement;
    if (stage) {
      this.graphResizeObserver.observe(stage);
    }
  }

  private resizeGraph(): void {
    const el = this.graphContainerEl;
    if (!this.graph || !el) return;
    const stage = el.parentElement;
    let width = el.clientWidth || stage?.clientWidth || 0;
    let height = el.clientHeight || stage?.clientHeight || 0;
    if (width <= 0 || height <= 0) {
      width = Math.max((typeof window !== 'undefined' ? window.innerWidth : 1280) - 320, 480);
      height = Math.max((typeof window !== 'undefined' ? window.innerHeight : 800) - 160, 480);
    }
    // The hierarchy uses world coordinates independent of viewport dimensions.
    // Resizing the canvas must preserve dragged positions and the current camera.
    this.graph.width(width).height(height);
    this.graph.refresh?.();
  }

  private async syncGraph(): Promise<void> {
    if (!this.graphData.nodes.length) return;
    if (!this.graphContainerEl) return;

    this.graphEngineError = '';

    await this.waitForContainerSize();
    await this.ensureGraph();

    if (!this.graph) {
      this.graphEngineError = 'Graph canvas could not be mounted. Retrying…';
      this.queueGraphSync();
      return;
    }

    this.recomputeFilteredData();
    this.ensureVisibleGraphData();
    this.pushGraphData();
    this.resizeGraph();
    this.applyForces();
    this.scheduleFocusRootView();
  }

  private scheduleFocusRootView(): void {
    if (this.selectedNode || this.highlightedNodeId) return;
    if (this.initialViewportApplied) return;
    if (this.viewMode === 'tree') {
      setTimeout(() => {
        if (!this.selectedNode && !this.highlightedNodeId) {
          this.focusRootNode(300);
        }
      }, 100);
      return;
    }
    [100, 400, 900, 1500].forEach((delay) => {
      setTimeout(() => {
        if (this.initialViewportApplied) return;
        if (!this.selectedNode && !this.highlightedNodeId) {
          this.focusRootNode(500);
        }
      }, delay);
    });
  }

  private getRenderedRootNode(): any | null {
    const nodes = this.graph?.graphData?.()?.nodes ?? [];
    if (!nodes.length) return null;
    return nodes.find((node: any) => node?.id === this.rootId) || nodes[0] || null;
  }

  private getInitialViewportNodeIds(maxHops: number): Set<string> {
    const rendered = new Set<string>(
      (this.graph?.graphData?.()?.nodes ?? [])
        .map((node: any) => String(node?.id || ''))
        .filter((id: string) => !!id),
    );
    const ids = new Set<string>();
    if (this.rootId && rendered.has(this.rootId)) ids.add(this.rootId);
    Object.entries(this.hopDistances).forEach(([id, dist]) => {
      if (rendered.has(id) && dist != null && Number(dist) <= maxHops) ids.add(id);
    });
    if (ids.size <= 1 && this.rootId) {
      (this.adjacency.get(this.rootId) ?? []).forEach((id) => {
        if (rendered.has(id)) ids.add(id);
      });
    }
    if (!ids.size) rendered.forEach((id) => ids.add(id));
    return ids;
  }

  private focusRootNode(duration = 400): void {
    if (!this.graph) return;
    this.resizeGraph();
    const root = this.getRenderedRootNode();
    if (!root || !Number.isFinite(root.x) || !Number.isFinite(root.y)) {
      this.fitGraphToView(duration);
      return;
    }

    const fitIds = this.getInitialViewportNodeIds(2);
    const width = this.graphContainerEl?.clientWidth || 1200;
    const height = this.graphContainerEl?.clientHeight || 720;
    const padding = Math.round(Math.min(width, height) * 0.07) + 40;

    this.graph.zoomToFit?.(0, padding, (node: any) => fitIds.has(node?.id));
    const fittedZoom = Number(this.graph.zoom?.() ?? 1);
    const zoom = Number.isFinite(fittedZoom)
      ? Math.min(1.45, Math.max(0.7, fittedZoom * 1.06))
      : 1.05;
    this.graph.zoom?.(zoom, duration);
    this.graph.refresh?.();
    this.initialViewportApplied = true;
  }

  private getGraphFitPadding(): number {
    return 130 + (this.isPanelOpen ? Math.round(this.effectiveDetailsPanelWidth * 0.5) : 0);
  }

  private fitGraphToView(duration = 400): void {
    if (!this.graph) return;
    this.resizeGraph();
    this.graph.zoomToFit?.(duration, this.getGraphFitPadding());
    this.graph.refresh?.();
  }

  /** Resize/redraw only — never change zoom/pan when the details panel opens. */
  private refreshGraphAfterPanelChange(): void {
    const refresh = (): void => {
      this.resizeGraph();
      this.graph?.refresh?.();
    };
    refresh();
    setTimeout(refresh, 0);
    setTimeout(refresh, 100);
  }

  private async waitForContainerSize(maxAttempts = 40): Promise<void> {
    for (let i = 0; i < maxAttempts; i++) {
      const el = this.graphContainerEl;
      if (el && el.clientWidth > 80 && el.clientHeight > 80) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  private async ensureGraph(): Promise<void> {
    this.invalidateGraphIfDetached();
    if (this.graph) return;
    if (!this.graphContainerEl) return;

    if (this.graphInitPromise) {
      try {
        await this.graphInitPromise;
      } catch {
        this.graphInitPromise = null;
      }
      if (this.graph) return;
      this.graphInitPromise = null;
    }

    this.graphInitPromise = this.initGraph();
    try {
      await this.graphInitPromise;
    } finally {
      if (!this.graph) {
        this.graphInitPromise = null;
      }
    }
  }

  private destroyGraph(): void {
    this.graph?._destructor?.();
    this.graph = undefined;
    this.graphInitPromise = null;
    this.initialViewportApplied = false;
    if (this.graphContainerEl) {
      this.graphContainerEl.innerHTML = '';
    }
  }

  private updateGraphData(): void {
    void this.syncGraph();
  }

  private getGraphBackgroundColor(): string {
    if (typeof document === 'undefined') {
      return this.isLightTheme ? '#ecedee' : '#081220';
    }

    const dashboard = this.graphContainerEl?.closest('.la-dashboard');
    const source = dashboard ?? this.graphContainerEl ?? document.body;
    const themed = getComputedStyle(source).getPropertyValue('--la-graph-bg').trim();
    if (themed && !themed.includes('gradient')) {
      return themed;
    }

    return this.isLightTheme ? '#ecedee' : '#081220';
  }

  private applyGraphTheme(): void {
    const background = this.getGraphBackgroundColor();
    if (this.graph?.backgroundColor) {
      this.graph.backgroundColor(background);
    }
    this.graph?.refresh?.();
    this.cdr.markForCheck();
  }

  private getNodeCategoryTextColor(): string {
    return this.isLightTheme ? '#8a9199' : '#9aa6b4';
  }

  private getNodeLabelTextColor(isActive: boolean): string {
    if (isActive) {
      return this.isLightTheme ? '#1f2933' : '#f1f5f9';
    }
    return this.isLightTheme ? '#3d4650' : '#d5dde6';
  }

  private getNodeSelectionStrokeColor(
    isSelected: boolean,
    isHighlighted: boolean,
    nodeColor: string,
  ): string {
    if (isSelected || isHighlighted) {
      return this.isLightTheme ? '#0f172a' : '#ffffff';
    }
    return nodeColor;
  }

  private getLinkLabelBackgroundColor(): string {
    return this.isLightTheme ? 'rgba(255, 255, 255, 0.92)' : 'rgba(8, 14, 24, 0.85)';
  }

  private getLinkLabelTextColor(): string {
    return this.isLightTheme ? '#0284c7' : '#7dd3fc';
  }

  private createForceGraphInstance(
    factory: (element: HTMLElement) => any,
    element: HTMLElement,
  ): any {
    try {
      return new (factory as unknown as new (element: HTMLElement) => any)(element);
    } catch {
      const builder = factory as unknown as () => (element: HTMLElement) => any;
      return builder()(element);
    }
  }

  private async resolveForceGraphFactory(): Promise<(element: HTMLElement) => any> {
    const globalFactory = (window as unknown as { ForceGraph?: (element: HTMLElement) => any })
      .ForceGraph;
    if (typeof globalFactory === 'function') {
      return globalFactory;
    }
    const mod = await import('force-graph');
    if (typeof mod.default !== 'function') {
      throw new Error('force-graph factory is unavailable');
    }
    return mod.default as unknown as (element: HTMLElement) => any;
  }

  private async initGraph(): Promise<void> {
    const container = this.graphContainerEl;
    if (!container || this.graph) {
      this.graphInitPromise = null;
      return;
    }

    if (container.querySelector('canvas')) {
      container.innerHTML = '';
    }

    try {
      await this.waitForContainerSize();
      if (!this.graphContainerEl || this.graph) return;

      const factory = await this.resolveForceGraphFactory();

      const el = this.graphContainerEl;
      const component = this;
      const width = Math.max(el.clientWidth, 400);
      const height = Math.max(el.clientHeight, 400);

      // Keep the animation loop and pointer processing out of Angular change detection.
      const graph = this.ngZone.runOutsideAngular(() => this.createForceGraphInstance(factory, el)
        .width(width)
        .height(height)
        .backgroundColor(this.getGraphBackgroundColor())
        .graphData({ nodes: [], links: [] })
        .nodeId('id')
        .linkSource('source')
        .linkTarget('target')
        .nodeCanvasObject((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
          try {
            component.paintNode(node, ctx, globalScale);
          } catch {
            const x = node?.x ?? 0;
            const y = node?.y ?? 0;
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, 2 * Math.PI);
            ctx.fillStyle = '#2aa8a0';
            ctx.fill();
          }
        })
        .nodePointerAreaPaint((node: any, color: string, ctx: CanvasRenderingContext2D) => {
          if (node.__searchJunctionKey) {
            const scale = Math.max(Number(component.graph?.zoom?.()) || 1, 0.001);
            const font = Math.min(18, Math.max(11, 10 / scale));
            ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(node.x, node.y, 12 / scale, 0, Math.PI * 2); ctx.fill();
            ctx.font = `600 ${font}px 'Segoe UI', sans-serif`;
            const width = ctx.measureText(node.label || '').width;
            ctx.fillRect(node.x - width / 2 - 6 / scale, node.y - 20 - font / 2 - 5 / scale,
              width + 12 / scale, font + 10 / scale);
            return;
          }
          const size = component.getPaintedNodeRadius(node);
          const x = node?.x ?? 0;
          const y = node?.y ?? 0;
          const bounds = getGraphPointerBounds(x, y, size);
          ctx.fillStyle = color;
          ctx.fillRect(bounds.left, bounds.top, bounds.width, bounds.height);
          if (component.viewMode === 'tree' && component.isCollapsibleHierarchyNode(node?.id)) {
            const badge = component.getBranchBadgeMetrics(node, size);
            ctx.beginPath();
            ctx.arc(badge.bx, badge.by, badge.badgeR + 1, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }
        })
        .linkCanvasObject((link: any, ctx: CanvasRenderingContext2D, globalScale: number) =>
          component.paintLink(link, ctx, globalScale),
        )
        .autoPauseRedraw(!this.animateLinks)
        .onRenderFramePre(() => { component.linkFlowSeconds = performance.now() / 1000; })
        .linkDirectionalParticles(0)
        .linkDirectionalParticleWidth((link: any) => {
          const sId = link?.source?.id ?? link?.source;
          const tId = link?.target?.id ?? link?.target;
          if (component.selectionFocusIds) {
            return component.isSelectionLinkActive(sId, tId) ? 2.5 : 0;
          }
          const focus = component.focusNeighbors;
          const focusId = component.focusId;
          return focus && focus.has(sId) && focus.has(tId) && (focusId === sId || focusId === tId)
            ? 2.5
            : 1.2;
        })
        .linkDirectionalParticleColor(() => 'rgba(245, 166, 35, 0.9)')
        .linkDirectionalParticleSpeed(0.006)
        .onNodeClick((node: any, event: MouseEvent) => {
          component.ngZone.run(() => {
            component.handleGraphNodeClick(node, event);
          });
        })
        .onNodeRightClick((node: any, event: MouseEvent) => {
          component.ngZone.run(() => {
            component.handleGraphNodeRightClick(node, event);
          });
        })
        .onNodeHover((node: any) => {
          component.ngZone.run(() => {
            if (node?.__searchJunctionKey) {
              component.hoverNodeId = null;
              component.hoverTooltipLabel = null;
              component.graph?.refresh?.();
              return;
            }
            if (!component.selectedNode) {
              component.hoverNodeId = node?.id ?? null;
            }
            component.updateHoverTooltip(node);
            component.graph?.refresh?.();
          });
        })
        .onZoom(() => {
          if (component.viewMode === 'tree') component.applyTreeZoomSpacing();
          if (component.hoverNodeId && component.hoverTooltipLabel && !component.selectedNode && component.graph) {
            const nodes = component.graph.graphData?.()?.nodes ?? [];
            const hovered = nodes.find((n: any) => n?.id === component.hoverNodeId);
            if (hovered) component.ngZone.run(() => component.updateHoverTooltip(hovered));
          }
        })
        .onBackgroundClick(() => {
          component.ngZone.run(() => {
            component.hoverNodeId = null;
            component.hoverTooltipLabel = null;
            component.closePanel();
          });
        })
        .onNodeDrag((node: any) => {
          if (component.viewMode !== 'tree') return;
          component.applySubtreeDrag(node);
        })
        .onNodeDragEnd((node: any) => {
          if (component.viewMode !== 'tree') return;
          component.commitSubtreeDrag(node);
        })
        .onDagError(() => undefined)
        .d3VelocityDecay(0.3)
        .d3AlphaDecay(0.02)
        .warmupTicks(80)
        .cooldownTicks(100)
        .enableNodeDrag(true)
        .onEngineStop(() => {
          if (component.viewMode === 'tree') return;
          component.snapshotPositionsForMode('network');
          if (!component.initialViewportApplied && !component.selectedNode && !component.highlightedNodeId) {
            component.focusRootNode(500);
          } else {
            component.resizeGraph();
            component.graph?.refresh?.();
          }
        }));

      this.graph = graph;
      this.graphEngineError = '';
      this.applyViewModeDag();
      this.resizeGraph();
      this.recomputeFilteredData();
      this.ensureVisibleGraphData();
      this.pushGraphData();
      this.scheduleFocusRootView();
    } catch (error) {
      console.error('Failed to initialize force-graph', error);
      this.graphEngineError = 'Graph engine failed to start. Retrying…';
      this.graph = undefined;
      this.queueGraphSync();
    } finally {
      this.graphInitPromise = null;
    }
  }

  private reinitGraphLayout(): void {
    if (!this.graph) return;
    this.applyViewModeDag();
    this.applyForces();
    this.pushGraphData();
  }

  private applyViewModeDag(): void {
    if (!this.graph) return;
    this.graph.linkDirectionalParticles(0).autoPauseRedraw(!this.animateLinks);
    if (this.viewMode === 'tree') {
      this.graph
        .dagMode(null as any)
        .linkDirectionalArrowLength(0)
        .warmupTicks(0)
        .cooldownTicks(0);
    } else {
      this.graph
        .dagMode(null as any)
        .linkDirectionalArrowLength(0)
        .warmupTicks(80)
        .cooldownTicks(100);
    }
  }

  private applyForces(): void {
    if (!this.graph) return;
    if (this.forceApplyTimer) clearTimeout(this.forceApplyTimer);

    if (this.viewMode === 'tree') {
      if (!this.treeNodePositions.size) {
        this.treeLayoutDirty = true;
      }
      this.pushGraphData();
      this.freezeTreeSimulation();
      setTimeout(() => {
        if (!this.initialViewportApplied && !this.selectedNode && !this.highlightedNodeId) {
          this.focusRootNode(300);
        }
      }, 60);
      return;
    }

    let attempts = 0;
    const apply = () => {
      const fg = this.graph;
      if (!fg?.d3Force) {
        if (attempts++ < 40) this.forceApplyTimer = setTimeout(apply, 100);
        return;
      }
      fg.d3Force('charge')?.strength(-1000)?.distanceMax?.(1100);
      fg.d3Force('link')?.distance(170)?.strength(0.45);
      void import('d3-force')
        .then(({ forceCollide }) => {
          fg.d3Force?.('collide', forceCollide(58));
          fg.d3ReheatSimulation?.();
          setTimeout(() => {
            if (!this.initialViewportApplied && !this.selectedNode && !this.highlightedNodeId) {
              this.focusRootNode(500);
            }
          }, 700);
        })
        .catch(() => {
          fg.d3ReheatSimulation?.();
          setTimeout(() => {
            if (!this.initialViewportApplied && !this.selectedNode && !this.highlightedNodeId) {
              this.focusRootNode(500);
            }
          }, 700);
        });
    };
    this.forceApplyTimer = setTimeout(apply, 150);
  }

  private computeTreeLayoutPositions(): Record<string, { x: number; y: number }> {
    const visibleIds = new Set(this.filteredNodes.map((node) => node.id));
    const prunedTree = pruneTreeForVisibleNodes(this.tree, visibleIds);
    const groups = groupSearchTrunks(this.filteredLinks, this.filteredNodes, this.rootId);
    const positions = computeSearchTrunkLayout(prunedTree, groups);
    const anchor = this.pendingTreeLayoutAnchor;
    if (anchor && positions[anchor.id]) {
      const dx = anchor.x - positions[anchor.id].x;
      const dy = anchor.y - positions[anchor.id].y;
      Object.values(positions).forEach((point) => { point.x += dx; point.y += dy; });
    }
    this.pendingTreeLayoutAnchor = null;
    this.treeTrunkRailOffsets.clear();
    groups.forEach((group) => {
      if (group.railOffset != null) this.treeTrunkRailOffsets.set(group.key, group.railOffset);
    });
    return positions;
  }

  private searchGroupRoute(group: SearchTrunkGroup<any>): ReturnType<typeof searchFanGeometry> | null {
    const source = group.links[0]?.source;
    const targets = group.links.map(link => link.target).filter(node => Number.isFinite(node?.x) && Number.isFinite(node?.y));
    if (!Number.isFinite(source?.x) || !Number.isFinite(source?.y) || !targets.length) return null;
    const placed = this.junctionPositions.get(group.key);
    const junction = this.searchJunctionNodes.get(group.key);
    const start = { x: source.x, y: source.y + this.getPaintedNodeRadius(source) + 2 };
    const siblings = [...this.searchJunctionNodes.values()]
      .filter(node => node.__searchJunctionGroup.sourceId === group.sourceId);
    const siblingTargets = siblings.flatMap(node => node.__searchJunctionGroup.links.map((link: any) => link.target))
      .filter(node => Number.isFinite(node?.x) && Number.isFinite(node?.y));
    const naturalContact = searchFanGeometry(start, siblingTargets.length ? siblingTargets : targets).stem[1];
    const contactY = Math.min(naturalContact.y, ...siblings.map(node => node.y - 30));
    const route = searchFanGeometry(
      start, targets,
      junction && Number.isFinite(junction.x) && Number.isFinite(junction.y)
        ? { x: junction.x, y: junction.y }
        : placed ? { x: placed.x * this.treeZoomSpreadApplied, y: placed.y } : undefined,
      Math.max(0, contactY - start.y),
    );
    return route;
  }

  private hierarchyDragMembers(node: any): string[] {
    const members = node.__searchJunctionKey
      ? searchGroupDragMembers(this.tree, node.__searchJunctionGroup)
      : collectTreeSubtreeIds(this.tree, node.id);
    this.searchJunctionNodes.forEach(junction => {
      if (members.has(junction.__searchJunctionGroup.sourceId)) members.add(junction.id);
    });
    if (node.__searchJunctionKey) members.add(node.id);
    return [...members];
  }

  /** Start/continue a rigid subtree drag for the hierarchy node being moved. */
  private applySubtreeDrag(node: any): void {
    if (!this.graph || !node?.id) return;

    if (!this.subtreeDragSession || this.subtreeDragSession.rootId !== node.id) {
      const memberIds = this.hierarchyDragMembers(node);
      const graphNodes: any[] = this.graph.graphData?.()?.nodes ?? [];
      const byId = new Map(graphNodes.map((n) => [n.id, n]));
      const origins = new Map<string, { x: number; y: number }>();
      memberIds.forEach((id) => {
        const saved = this.treeNodePositions.get(id);
        const live = byId.get(id);
        if (saved) {
          origins.set(id, { x: saved.x, y: saved.y });
        } else if (live) {
          origins.set(id, { x: Number(live.x) || 0, y: Number(live.y) || 0 });
        }
      });
      const dragStart = origins.get(node.id) ?? {
        x: Number(node.x) || 0,
        y: Number(node.y) || 0,
      };
      this.subtreeDragSession = { rootId: node.id, memberIds, origins, dragStart };
    }

    const session = this.subtreeDragSession;
    const dx = (Number(node.x) || 0) - session.dragStart.x;
    const dy = (Number(node.y) || 0) - session.dragStart.y;
    const graphNodes: any[] = this.graph.graphData?.()?.nodes ?? [];
    const byId = new Map(graphNodes.map((n) => [n.id, n]));

    session.memberIds.forEach((id) => {
      const origin = session.origins.get(id);
      const n = byId.get(id);
      if (!origin || !n) return;
      if (id === node.id) {
        n.fx = node.x;
        n.fy = node.y;
        if (n.__searchJunctionKey) this.junctionPositions.set(n.__searchJunctionKey, {x:n.x / this.treeZoomSpreadApplied,y:n.y});
        return;
      }
      n.x = origin.x + dx;
      n.y = origin.y + dy;
      n.fx = n.x;
      n.fy = n.y;
      if (n.__searchJunctionKey) this.junctionPositions.set(n.__searchJunctionKey, {x:n.x / this.treeZoomSpreadApplied,y:n.y});
    });

    this.graph.refresh?.();
  }

  /** Persist positions for the dragged node and all moved descendants. */
  private commitSubtreeDrag(node: any): void {
    if (!this.graph || !node?.id) return;

    const session = this.subtreeDragSession;
    const memberIds =
      session && session.rootId === node.id
        ? session.memberIds
        : this.hierarchyDragMembers(node);

    const spread = Math.max(this.treeZoomSpreadApplied || 1, 0.001);
    const graphNodes: any[] = this.graph.graphData?.()?.nodes ?? [];
    const byId = new Map(graphNodes.map((n) => [n.id, n]));

    // Ensure the primary dragged node is pinned at its drop position.
    node.fx = node.x;
    node.fy = node.y;

    memberIds.forEach((id) => {
      const n = byId.get(id) ?? (id === node.id ? node : null);
      if (!n) return;
      const x = Number(n.x) || 0;
      const y = Number(n.y) || 0;
      n.fx = x;
      n.fy = y;
      this.treeCompactPositions.set(id, { x: x / spread, y });
      this.treeNodePositions.set(id, { x, y, fx: x, fy: y });
      if (n.__searchJunctionKey) this.junctionPositions.set(n.__searchJunctionKey, {x:x / spread,y});
    });

    this.subtreeDragSession = null;
    this.freezeTreeSimulation();
    this.graph.refresh?.();
  }

  private getTreeZoomSpreadFactor(zoom: number): number {
    if (zoom < this.treeZoomGapThreshold) return 1;
    // Very small extra gap only after zoom-in (caps at ~10% wider).
    return Math.min(1.1, 1 + (zoom - this.treeZoomGapThreshold) * 0.07);
  }

  private applyTreeZoomSpacing(force = false): void {
    if (this.subtreeDragSession) return;
    if (this.viewMode !== 'tree' || !this.graph || !this.treeCompactPositions.size) return;
    const zoom = Number(this.graph.zoom?.() ?? 1);
    const spread = this.getTreeZoomSpreadFactor(zoom);
    if (!force && Math.abs(spread - this.treeZoomSpreadApplied) < 0.00001) return;
    this.treeZoomSpreadApplied = spread;

    const data = this.graph.graphData?.();
    if (!data?.nodes?.length) return;

    data.nodes.forEach((node: any) => {
      const base = this.treeCompactPositions.get(node.id);
      if (!base) return;
      const x = base.x * spread;
      const y = base.y;
      node.x = x;
      node.y = y;
      node.fx = x;
      node.fy = y;
      this.treeNodePositions.set(node.id, { x, y, fx: x, fy: y });
    });
    this.graph.refresh?.();
  }

  private freezeTreeSimulation(): void {
    if (!this.graph) return;
    this.graph.d3AlphaTarget?.(0);
    this.graph.d3AlphaDecay?.(1);
    this.graph.d3Force?.('charge', null);
    this.graph.d3Force?.('link', null);
    this.graph.d3Force?.('collide', null);
  }

  private clearPinnedTreeNodes(): void {
    if (!this.graph?.graphData) return;
    const data = this.graph.graphData();
    let changed = false;
    data.nodes?.forEach((node: any) => {
      if (node.fx != null || node.fy != null) {
        delete node.fx;
        delete node.fy;
        changed = true;
      }
    });
    if (changed) {
      this.graph.graphData(data);
    }
  }

  private findTreeNode(tree: TreeNode | null, nodeId: string): TreeNode | null {
    if (!tree) return null;
    if (tree.node.id === nodeId) return tree;
    for (const child of tree.children) {
      const found = this.findTreeNode(child, nodeId);
      if (found) return found;
    }
    return null;
  }

  isCollapsibleHierarchyNode(nodeId: string): boolean {
    return this.viewMode === 'tree' && this.isCollapsibleBranchNode(nodeId);
  }

  private isCollapsibleBranchNode(nodeId: string): boolean {
    if (!this.tree) return false;
    if (this.indexedTree !== this.tree) {
      this.hierarchyNodeIndex = new Map();
      const visit = (node: TreeNode): void => {
        this.hierarchyNodeIndex.set(node.node.id, node);
        node.children.forEach(visit);
      };
      visit(this.tree);
      this.indexedTree = this.tree;
    }
    const treeNode = this.hierarchyNodeIndex.get(nodeId);
    if (!treeNode || treeNode.depth < 1 || !treeNode.children.length) return false;
    if (isIdentityHubNode(treeNode.node) || String(nodeId).startsWith('identity-hub:')) {
      return false;
    }

    if (String(nodeId).startsWith('source-hub:')) return true;

    const raw = treeNode.node.raw || {};
    if (isSourceTypeHubNode(raw)) return true;

    const type = String(raw.type || treeNode.node.type || '').toLowerCase();
    if (type === 'ai_analysis' || type === 'leak_source') return true;

    const sourceType = resolveNodeSourceType(raw);
    return LinkAnalysisGraphDashboardComponent.COLLAPSIBLE_SOURCE_TYPES.has(sourceType);
  }

  private getHierarchyHiddenNodeIds(): Set<string> {
    const hidden = new Set<string>();
    if (!this.tree) return hidden;

    const walk = (treeNode: TreeNode, parentCollapsed: boolean) => {
      if (parentCollapsed) {
        hidden.add(treeNode.node.id);
      }
      const branchCollapsed =
        this.isCollapsibleBranchNode(treeNode.node.id) &&
        !this.expandedBranchIds.has(treeNode.node.id);
      const hideChildren = parentCollapsed || branchCollapsed;
      treeNode.children.forEach((child) => walk(child, hideChildren));
    };

    walk(this.tree, false);
    return hidden;
  }

  private snapshotPositionsForMode(mode: ViewMode): void {
    if (!this.graph?.graphData) return;
    const nodes = this.graph.graphData()?.nodes ?? [];
    if (!nodes.length) return;

    if (mode === 'tree') {
      this.treeNodePositions.clear();
      nodes.forEach((node: any) => {
        if (node?.id == null || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
        this.treeNodePositions.set(node.id, {
          x: node.x,
          y: node.y,
          fx: node.fx ?? node.x,
          fy: node.fy ?? node.y,
        });
      });
      return;
    }

    this.networkNodePositions.clear();
    nodes.forEach((node: any) => {
      if (node?.id == null || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return;
      this.networkNodePositions.set(node.id, { x: node.x, y: node.y });
    });
  }

  private preloadImages(): void {
    if (typeof window === 'undefined') return;
    const urls = new Set<string>();
    this.graphData.nodes.forEach((node) => {
      const brandUrl = this.getNodeBrandIconUrl(node);
      if (brandUrl) urls.add(brandUrl);
      const photoUrl = this.getGraphNodePhotoUrl(node);
      if (photoUrl) urls.add(photoUrl);
    });
    urls.forEach((url) => {
      if (this.imageCache.has(url)) return;
      this.cacheImageElement(url, url);
    });
  }

  /**
   * Same pipeline as OSINT phone / email / username / name result pages:
   * collect `data_list[0].image_url`, then fetch `/api/osint/image/...` with Bearer
   * via SearchResultProcessorService.extractImagesFromSources.
   */
  private refreshOsintGalleryImages(): void {
    const payload = this.collectOsintImagePayload();
    const imageUrls: string[] = payload?.data_list?.[0]?.image_url || [];
    const signature = [...imageUrls].sort().join('|');
    if (signature === this.osintImageUrlSignature) return;

    this.osintImageUrlSignature = signature;
    this.osintImagesSub?.unsubscribe();
    if (!imageUrls.length) {
      this.osintGalleryImages = [];
      return;
    }

    this.osintImagesSub = this.searchResultProcessor.extractImagesFromSources(payload).subscribe({
      next: (images) => {
        this.ngZone.run(() => {
          this.osintGalleryImages = this.annotateGalleryPlatforms(images);
          this.cdr.markForCheck();
          this.preloadImages();
          this.graph?.refresh?.();
          this.galleryUpdated.emit();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.osintGalleryImages = [];
          this.cdr.markForCheck();
        });
      },
    });
  }

  private collectOsintImagePayload(): { data_list: Array<{ image_url: string[] }> } {
    const imageUrls: string[] = [];
    const seen = new Set<string>();
    const platformImageUrls = new Map<string, string[]>();

    const addUrl = (value: unknown, platform?: unknown) => {
      const url = this.extractImageUrlValue(value);
      if (!url) return;
      if (!seen.has(url)) {
        seen.add(url);
        imageUrls.push(url);
      }
      const platformKey = this.normalizePlatformNameForLookup(platform);
      if (!platformKey) return;
      const list = platformImageUrls.get(platformKey) || [];
      if (!list.includes(url)) list.push(url);
      platformImageUrls.set(platformKey, list);
    };

    const ingestSnapshot = (snapshot: any) => {
      if (!snapshot || typeof snapshot !== 'object') return;
      (Array.isArray(snapshot.image_url) ? snapshot.image_url : []).forEach((url: unknown) =>
        addUrl(url),
      );
      const sources = snapshot.sources;
      if (sources && typeof sources === 'object') {
        Object.entries(sources).forEach(([platform, sourceData]) => {
          this.collectSourceImageUrls(sourceData, platform, addUrl);
        });
      }
      (Array.isArray(snapshot.image_hash) ? snapshot.image_hash : []).forEach((hash: unknown) => {
        addUrl(this.reconstructOsintImageUrl(hash, imageUrls));
      });
    };

    const nodes = [
      ...(this.graphPayload?.nodes || []),
      ...this.graphData.nodes.map((node) => node.raw).filter(Boolean),
    ];

    nodes.forEach((node: any) => {
      const metadata = node?.metadata || {};
      ingestSnapshot(metadata.normalizedResult);
      const platform = metadata.platform || metadata.sourceName;
      this.collectSourceImageUrls(metadata.sourceData, platform, addUrl);
      addUrl(node?.imageUrl, platform);
      addUrl(metadata.imageUrl, platform);
      addUrl(metadata.previewImageUrl, platform);
      addUrl(metadata.sourceImageUrl, platform);
      addUrl(metadata.relatedImageUrl, platform);
      addUrl(metadata.queryValue, platform);
      addUrl(metadata.thumbnail, platform);
      getNodeSearchableValues(node).image.forEach((url) => addUrl(url, platform));
    });

    this.graphData.nodes.forEach((node) => {
      const platform = node.raw?.metadata?.platform || node.raw?.metadata?.sourceName;
      addUrl(node.image, platform);
    });

    nodes.forEach((node: any) => {
      const metadata = node?.metadata || {};
      const platform = metadata.platform || metadata.sourceName;
      addUrl(
        this.reconstructOsintImageUrl(metadata.sourceData?.fields?.image_hash?.value, imageUrls),
        platform,
      );
    });

    this.platformImageUrls = platformImageUrls;
    return { data_list: [{ image_url: imageUrls }] };
  }

  private reconstructOsintImageUrl(hash: unknown, knownUrls: string[]): string | null {
    const trimmed = String(hash ?? '').trim();
    if (!trimmed) return null;
    const sample = knownUrls.find((url) => /\/api\/osint\/image\//i.test(url));
    if (sample) {
      return toAbsoluteOsintImageUrl(
        sample.replace(/\/api\/osint\/image\/[^/?#]+/i, `/api/osint/image/${trimmed}`),
      );
    }
    return toAbsoluteOsintImageUrl(trimmed);
  }

  private collectSourceImageUrls(
    sourceData: any,
    platform: unknown,
    addUrl: (value: unknown, platform?: unknown) => void,
  ): void {
    const fields = sourceData?.fields;
    if (!fields || typeof fields !== 'object') return;
    const pictureKeys = [
      'picture',
      'picture_url',
      'image_url',
      'avatar',
      'avatar_url',
      'photo',
      'photo_url',
      'profile_image',
      'profile_picture',
      'thumbnail',
    ];
    pictureKeys.forEach((key) => addUrl(fields[key], platform));
  }

  private extractImageUrlValue(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (
        trimmed.startsWith('http') ||
        trimmed.startsWith('data:image/') ||
        /^\/api\/osint\/image\//i.test(trimmed) ||
        isAuthGatedOsintImageUrl(trimmed)
      ) {
        // Match OSINT search: relative proxy → environment.baseUrl host (:3000), not :4200
        return toAbsoluteOsintImageUrl(trimmed);
      }
      return null;
    }
    if (value && typeof value === 'object' && typeof (value as any).value === 'string') {
      return this.extractImageUrlValue((value as any).value);
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.extractImageUrlValue(item);
        if (found) return found;
      }
    }
    return null;
  }

  private annotateGalleryPlatforms(images: SearchImage[]): SearchImage[] {
    return images.map((image) => {
      const alreadyNamed = this.normalizePlatformNameForLookup(image.platform);
      if (alreadyNamed && alreadyNamed !== 'image') return image;
      for (const [platform, urls] of this.platformImageUrls.entries()) {
        if (urls.some((url) => this.urlsReferToSameOsintImage(image.detectUrl, url))) {
          const name = platform
            .split('_')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');
          return { ...image, platform: name, name };
        }
      }
      return image;
    });
  }

  private cacheImageElement(cacheKey: string, src: string): void {
    if (typeof window === 'undefined' || !src || this.imageCache.has(cacheKey) ||
        this.imageRequests.get(cacheKey) === src) return;
    // Remember pending and failed requests too: an image failure must not start a redraw/retry loop.
    this.imageRequests.set(cacheKey, src);
    const img = new window.Image();
    img.onload = () => {
      this.imageCache.set(cacheKey, img);
      this.imagesReady += 1;
      this.graph?.refresh?.();
    };
    img.onerror = () => {
      this.graph?.refresh?.();
    };
    img.src = src;
  }

  private paintCircularNodeImage(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    size: number,
    nodeColor: string,
    isSelected: boolean,
    isHighlighted: boolean,
    tagColor: string | null,
    options?: { padded?: boolean },
  ): void {
    const padded = options?.padded === true;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.fillStyle = nodeColor;
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.clip();
    if (padded) {
      const inset = size * 0.34;
      ctx.drawImage(img, x - size + inset, y - size + inset, (size - inset) * 2, (size - inset) * 2);
    } else {
      ctx.drawImage(img, x - size, y - size, size * 2, size * 2);
    }
    ctx.restore();

    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.lineWidth = isSelected || isHighlighted ? 2 : 1.4;
    ctx.strokeStyle =
      tagColor ?? this.getNodeSelectionStrokeColor(isSelected, isHighlighted, `${nodeColor}`);
    ctx.stroke();

    if (tagColor) {
      ctx.beginPath();
      ctx.arc(x + size * 0.72, y - size * 0.72, Math.max(size * 0.32, 2), 0, 2 * Math.PI);
      ctx.fillStyle = tagColor;
      ctx.strokeStyle = '#0b1220';
      ctx.lineWidth = 0.8;
      ctx.fill();
      ctx.stroke();
    }
  }

  private paintNode(node: any, ctx: CanvasRenderingContext2D, globalScale: number): void {
    if (node.__searchJunctionKey) {
      ctx.save();
      ctx.beginPath(); ctx.arc(node.x, node.y, 5 / globalScale, 0, Math.PI * 2);
      ctx.fillStyle = this.getLinkLabelBackgroundColor(); ctx.fill();
      ctx.strokeStyle = this.getLinkLabelTextColor(); ctx.lineWidth = 1.5 / globalScale; ctx.stroke();
      ctx.restore(); return;
    }
    ctx.globalAlpha = 1;
    const id = node?.id ?? '';
    const type = node?.type as EntityType;
    const count = this.degreeMap[id] ?? 1;
    const size = this.getPaintedNodeRadius(node);
    const iconColor = getGraphIconColor(type);
    const tag = this.annotationsMap[id]?.tag;
    const tagColor = tag ? TAG_META[tag].color : null;
    const isHighlighted = this.highlightedNodeId === id;
    const isSelected = this.selectedNode?.id === id;
    const isHover = this.hoverNodeId === id;
    const hasCorrHL = this.correlationHighlight.size > 0;
    const selectionFocus = this.selectionFocusIds;
    const focusNeighbors = this.focusNeighbors;
    const focusId = this.focusId;

    const dimGraph = this.shouldDimGraph && !!focusNeighbors;
    const inFocus =
      (!dimGraph || focusNeighbors!.has(id)) &&
      (!hasCorrHL || this.correlationHighlight.has(id));
    const x = node?.x ?? 0;
    const y = node?.y ?? 0;

    ctx.globalAlpha = dimGraph && !inFocus ? 0.2 : 1;

    if (isHighlighted || isSelected || isHover || selectionFocus?.has(id)) {
      paintSelectionHalo(
        ctx,
        x,
        y,
        size,
        isSelected || isHighlighted ? GRAPH_EDGE_ACTIVE : 'rgba(245, 166, 35, 0.55)',
      );
    }

    const viewerNode = node as ViewerNode;
    const photoUrl = this.getGraphNodePhotoUrl(viewerNode);
    const photoImg = photoUrl ? this.imageCache.get(photoUrl) : undefined;
    const brandIconUrl = this.getNodeBrandIconUrl(viewerNode);
    const brandImg = brandIconUrl ? this.imageCache.get(brandIconUrl) : undefined;
    const platformKey = this.getNodePlatformKey(viewerNode);
    const rawType = String(
      viewerNode.raw?.metadata?.hubSourceType || viewerNode.raw?.type || type,
    );

    if (photoImg) {
      paintSquarePhoto(ctx, photoImg, x, y, size);
    } else if (this.isPlatformBrandNode(viewerNode) && platformKey) {
      if (brandImg) {
        paintBrandGlyph(ctx, brandImg, x, y, size);
      } else {
        paintPlatformBrandIcon(ctx, platformKey, x, y, size);
      }
    } else {
      paintGraphTypeIcon(ctx, rawType, x, y, size, iconColor);
    }

    if (tagColor) {
      paintBookmarkRibbon(ctx, x, y, size, tagColor);
    }

    const isImportant = type === 'person' || type === 'phone' || type === 'email' || count >= 4;
    const isActive =
      isSelected ||
      isHighlighted ||
      isHover ||
      (!!focusNeighbors?.has(id) && focusId != null);
    const showLabel = this.shouldShowNodeLabel(id, inFocus, isActive, globalScale, isImportant);
    if (showLabel) {
      this.paintNodeNameLabel(ctx, viewerNode, x, y, size, globalScale, isActive);
    }

    this.paintBranchLayerBadge(id, node, size, ctx, globalScale);
    ctx.globalAlpha = 1;
  }

  private paintBranchLayerBadge(
    nodeId: string,
    node: any,
    size: number,
    ctx: CanvasRenderingContext2D,
    globalScale: number,
  ): void {
    if (this.viewMode !== 'tree' || !this.isCollapsibleHierarchyNode(nodeId)) return;

    const branchNumber = this.getBranchLayerNumber(nodeId);
    if (!branchNumber) return;

    const expanded = this.expandedBranchIds.has(nodeId);
    const branch = this.branchLayers.find((b) => b.id === nodeId);
    const { bx, by, badgeR } = this.getBranchBadgeMetrics(node, size);

    ctx.beginPath();
    ctx.arc(bx, by, badgeR, 0, 2 * Math.PI);
    ctx.fillStyle = expanded ? branch?.color ?? '#16a34a' : '#6b7280';
    ctx.fill();
    ctx.strokeStyle = '#0b1220';
    ctx.lineWidth = Math.max(1 / globalScale, 0.65);
    ctx.stroke();

    ctx.font = `bold ${Math.max(badgeR * 1.15, 5.5)}px sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(branchNumber), bx, by + 0.5 / globalScale);
  }

  private getLinkPaintStyle(scale: number, active: boolean, dim: boolean): { color: string; width: number } {
    const color = active ? GRAPH_EDGE_ACTIVE : dim ?
      (this.isLightTheme ? 'rgba(100, 116, 139, 0.20)' : 'rgba(148, 163, 184, 0.18)') :
      (this.isLightTheme ? GRAPH_EDGE_COLOR_LIGHT : GRAPH_EDGE_COLOR_DARK);
    // Compensate for canvas zoom so overview links retain a readable pixel width.
    const zoomCompensation = Math.max(1, 1 / Math.max(scale, 0.01));
    return { color, width: (active ? 1.8 : dim ? 0.65 : 0.85) * zoomCompensation };
  }

  private paintLinkFlow(ctx: CanvasRenderingContext2D, points: Array<{x: number; y: number}>, scale: number, color: string, routeKey: object): void {
    if (!this.animateLinks) return;
    let state = this.linkFlowStates.get(routeKey);
    if (!state) {
      state = {progress: 0};
      this.linkFlowStates.set(routeKey, state);
    }
    const arrow = linkFlowArrow(points, this.linkFlowSeconds, scale, state);
    if (!arrow) return;
    paintArrowHead(ctx, arrow.x - arrow.dx, arrow.y - arrow.dy, arrow.x, arrow.y,
      Math.max(7 / scale, 4.2), color);
  }

  private paintSearchTrunk(group: SearchTrunkGroup<any>, ctx: CanvasRenderingContext2D, scale: number): void {
    const links = group.links;
    if (!links.length) return;
    const source = links[0].source;
    if (!Number.isFinite(source?.x) || !Number.isFinite(source?.y)) return;
    const targets = links.filter((link) => Number.isFinite(link.target?.x) && Number.isFinite(link.target?.y));
    if (!targets.length) return;
    ctx.save();
    const fontSize = Math.min(18, Math.max(11, 10 / scale));
    ctx.font = `600 ${fontSize}px 'Segoe UI', sans-serif`;
    const route = this.searchGroupRoute(group);
    if (!route) { ctx.restore(); return; }
    const isActive = (link: any): boolean => {
      const s = graphEndpointId(link.source);
      const t = graphEndpointId(link.target);
      return this.selectionFocusIds ? this.isSelectionLinkActive(s, t) :
        !!this.focusNeighbors?.has(s) && !!this.focusNeighbors?.has(t) && (this.focusId === s || this.focusId === t);
    };
    const style = (active: boolean): string => {
      const dim = this.shouldDimGraph && !!this.focusNeighbors && !active;
      const { color, width } = this.getLinkPaintStyle(scale, active, dim);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.setLineDash([]);
      return color;
    };
    const strokePath = (points: Array<{ x: number; y: number }>): void => {
      ctx.beginPath();
      points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
      ctx.stroke();
    };
    const anyActive = targets.some(isActive);
    const junctionColor = style(anyActive);
    strokePath(route.stem);
    this.paintLinkFlow(ctx, route.stem, scale, junctionColor, group);
    targets.forEach((link) => {
      const target = link.target;
      const arrowSize = Math.max(6.5 / scale, 4.2);
      const color = style(isActive(link));
      const segment = insetLine(route.junction.x, route.junction.y, target.x, target.y,
        0, this.getPaintedNodeRadius(target) + arrowSize * 0.35);
      if (!segment) return;
      strokePath([{ x: segment.x1, y: segment.y1 }, { x: segment.x2, y: segment.y2 }]);
      paintArrowHead(ctx, segment.x1, segment.y1, segment.x2, segment.y2, arrowSize, color);
      this.paintLinkFlow(ctx, [{x: segment.x1, y: segment.y1}, {x: segment.x2, y: segment.y2}], scale, color, link);
    });
    ctx.beginPath();
    ctx.arc(route.junction.x, route.junction.y, 4.5 / scale, 0, Math.PI * 2);
    ctx.fillStyle = this.getLinkLabelBackgroundColor();
    ctx.fill();
    ctx.strokeStyle = junctionColor;
    ctx.lineWidth = 1.5 / scale;
    ctx.stroke();
    if (group.label) {
      ctx.globalAlpha = this.shouldDimGraph && !targets.some(isActive) ? 0.25 : 1;
      const { x, y } = route.label;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const width = ctx.measureText(group.label).width;
      ctx.fillStyle = this.getLinkLabelBackgroundColor();
      ctx.fillRect(x - width / 2 - 5, y - fontSize / 2 - 3, width + 10, fontSize + 6);
      ctx.fillStyle = this.getLinkLabelTextColor();
      ctx.fillText(group.label, x, y);
    }
    ctx.restore();
  }

  private paintLink(link: any, ctx: CanvasRenderingContext2D, globalScale: number): void {
    const trunk = this.viewMode === 'tree' ? this.searchTrunkByLink.get(link) : undefined;
    if (trunk) {
      if (trunk.links[0] === link) this.paintSearchTrunk(trunk, ctx, globalScale);
      return;
    }
    const sx = link?.source?.x ?? 0;
    const sy = link?.source?.y ?? 0;
    const tx = link?.target?.x ?? 0;
    const ty = link?.target?.y ?? 0;
    const sId = link?.source?.id ?? link?.source;
    const tId = link?.target?.id ?? link?.target;
    const focusNeighbors = this.focusNeighbors;
    const focusId = this.focusId;
    const selectionFocus = this.selectionFocusIds;

    const active =
      selectionFocus != null
        ? this.isSelectionLinkActive(sId, tId)
        : focusNeighbors &&
        focusNeighbors.has(sId) &&
        focusNeighbors.has(tId) &&
        (focusId === sId || focusId === tId);
    const dim = this.shouldDimGraph && focusNeighbors && !active;

    const srcSize = this.getPaintedNodeRadius(link.source);
    const tgtSize = this.getPaintedNodeRadius(link.target);
    const arrowSize = Math.max(6.5 / globalScale, 4.2);
    const segment = insetLine(sx, sy, tx, ty, srcSize + 1.5, tgtSize + arrowSize * 0.35);
    if (!segment) return;

    const { color: stroke, width } = this.getLinkPaintStyle(globalScale, !!active, !!dim);

    // Straight fan lines (reference hierarchy style).
    ctx.beginPath();
    ctx.moveTo(segment.x1, segment.y1);
    ctx.lineTo(segment.x2, segment.y2);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.setLineDash([]);
    paintArrowHead(ctx, segment.x1, segment.y1, segment.x2, segment.y2, arrowSize, stroke);
    this.paintLinkFlow(ctx, [{x: segment.x1, y: segment.y1}, {x: segment.x2, y: segment.y2}], globalScale, stroke, link);

    if (active && globalScale > 1.2 && link?.label) {
      const mx = (segment.x1 + segment.x2) / 2;
      const my = (segment.y1 + segment.y2) / 2;
      const fontSize = Math.max(8 / globalScale, 2.8);
      ctx.font = `${fontSize}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const w = ctx.measureText(link.label).width;
      ctx.fillStyle = this.getLinkLabelBackgroundColor();
      ctx.fillRect(mx - w / 2 - 2 / globalScale, my - fontSize / 2 - 1 / globalScale, w + 4 / globalScale, fontSize + 2 / globalScale);
      ctx.fillStyle = this.getLinkLabelTextColor();
      ctx.fillText(link.label, mx, my);
    }
  }

  private applyTimelineFilter(): void {
    if (this.timelineStepIndex >= this.timelineDates.length - 1) {
      this.timelineVisibleIds = null;
    } else {
      const cutoff = this.timelineDates[this.timelineStepIndex];
      const ids = new Set<string>();
      this.graphData.nodes.forEach((n) => {
        if (n.firstSeen && String(n.firstSeen).slice(0, 10) <= cutoff) ids.add(n.id);
      });
      this.timelineVisibleIds = ids;
    }
    const cur = this.timelineDates[this.timelineStepIndex];
    this.timelineNewAtStep = this.graphData.nodes.filter(
      (n) => String(n.firstSeen).slice(0, 10) === cur,
    );
    this.recomputeFilteredData();
    this.pushGraphData();
  }

  private refreshCmdItems(): void {
    const q = this.cmdQuery.toLowerCase().trim();
    const actions = this.paletteActions
      .filter((a) => !q || a.label.toLowerCase().includes(q))
      .map((a) => ({
        type: 'action' as const,
        label: a.label,
        hint: a.hint,
        run: a.run,
      }));
    const nodes = this.graphData.nodes
      .filter((n) => !q || n.label.toLowerCase().includes(q) || ENTITY_LABELS[n.type].toLowerCase().includes(q))
      .slice(0, 20)
      .map((n) => ({
        type: 'node' as const,
        label: n.label,
        hint: ENTITY_LABELS[n.type],
        run: () => this.selectNode(n.id),
      }));
    this.cmdItems = [...actions, ...nodes];
  }

  private downloadFile(filename: string, content: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private async initMap(): Promise<void> {
    const token = ++this.mapInitToken;
    if (!this.mapContainer?.nativeElement || this.map) {
      this.mapLoading = false;
      return;
    }

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [25, 80],
      zoom: 4,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
    }).addTo(this.map);

    try {
      const markers = await collectGraphMapMarkers(this.graphData);
      if (token !== this.mapInitToken || !this.map) return;
      this.mapMarkerCount = markers.length;
      this.renderMapMarkers(markers);
    } finally {
      if (token === this.mapInitToken) {
        this.mapLoading = false;
        this.cdr.detectChanges();
      }
      setTimeout(() => this.map?.invalidateSize(), 250);
    }
  }

  private renderMapMarkers(markers: GraphMapMarker[]): void {
    if (!this.map) return;

    const nodeMap = new Map(this.graphData.nodes.map((n) => [n.id, n]));
    const latlngs: L.LatLngExpression[] = [];

    markers.forEach((marker) => {
      latlngs.push([marker.lat, marker.lng]);
      const color = ENTITY_COLORS[marker.entityType] ?? ENTITY_COLORS.location;
      const icon = L.divIcon({
        className: 'osint-pin',
        html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.5);"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 22],
        popupAnchor: [0, -22],
      });

      const linkedHtml = marker.nodeIds
        .map((id) => ({ id, lbl: nodeMap.get(id)?.label ?? id }))
        .map(
          ({ id, lbl }) =>
            `<span data-node="${id}" style="display:inline-block;background:#1e293b;color:#cbd5e1;border-radius:4px;padding:2px 7px;margin:2px;font-size:11px;cursor:pointer;">${lbl}</span>`,
        )
        .join('');

      const sourceLabel =
        marker.sourceKind === 'geocoded'
          ? 'Geocoded from location text'
          : marker.sourceKind === 'map_data'
            ? 'Map pin from source'
            : 'Coordinates';

      const popupHtml = `<div style="font-family:sans-serif;min-width:190px;">
        <div style="font-weight:700;font-size:13px;color:#0f172a;">${marker.title}</div>
        <div style="font-size:11px;color:#64748b;margin-bottom:4px;">${marker.subtitle}</div>
        <div style="font-size:10px;color:#94a3b8;margin-bottom:6px;">${sourceLabel}</div>
        <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:3px;">Linked entities (${marker.nodeIds.length})</div>
        <div>${linkedHtml}</div>
      </div>`;

      const leafletMarker = L.marker([marker.lat, marker.lng], { icon }).addTo(this.map!).bindPopup(popupHtml);
      leafletMarker.on('dblclick', () => {
        this.closeMap();
        this.selectNode(marker.primaryNodeId);
      });
      leafletMarker.on('popupopen', (e) => {
        const el = e.popup.getElement();
        if (!el) return;
        el.querySelectorAll('[data-node]').forEach((nodeEl) => {
          nodeEl.addEventListener('click', () => {
            const id = nodeEl.getAttribute('data-node');
            if (id) {
              this.closeMap();
              this.selectNode(id);
            }
          });
        });
      });
    });

    if (latlngs.length > 0) {
      this.map.fitBounds(L.latLngBounds(latlngs), { padding: [80, 80], maxZoom: 12 });
    }
  }

  private destroyMap(): void {
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }
}
